window.PXUTheme = window.PXUTheme || {};

// ==========================================================================
//  SubscriptionBlockController
//  Per-block controller for the global subscription block.
//  Instances are created by jsGlobalProduct.initSubscriptionBlocks() from
//  config objects pushed to window._subscriptionBlockQueue by the inline
//  Liquid script in p_global_subscription.liquid.
// ==========================================================================
class SubscriptionBlockController {
  constructor(blockEl, config) {
    this.blockEl = blockEl;
    this.blockId = blockEl.id;
    this.config = config;

    // Flags from data-* attributes
    this.defaultSelection = blockEl.dataset.defaultSelection || 'none';
    this.isSellingPlanOnly = blockEl.dataset.sellingPlanOnly === 'true';
    this.useColorMatch = blockEl.dataset.colorMatch === 'true';
    this.isAddon = blockEl.dataset.isAddon === 'true';
    this.hideNoSub = blockEl.dataset.hideNoSub === 'true';

    // Liquid-injected values passed from inline script
    this.subscribeAtcText = config.subscribeAtcText || '';
    this.variantColorMap = config.variantColorMap || {};

    this.hasAdditionalOffer = blockEl.dataset.additionalOffer === 'true';
    this.originalAtcLabel = null;
    this._cleanup = [];

    // Variant → plan filtering (SPO mode)
    this._variantPlanMap = null;
    this._lastVariantId = null;
    this._lastHadPlans = undefined;
    if (this.isSellingPlanOnly && blockEl.dataset.variantPlanLinks) {
      this._variantPlanMap = this._parseVariantPlanLinks(
        blockEl.dataset.variantPlanLinks,
        blockEl.dataset.universalPlanIndices || ''
      );
      // Seed with the Liquid-rendered initial variant so onVariantChanged
      // skips redundant DOM work when JS detects the same variant on init.
      const ssrVid = blockEl.dataset.initialVariantId;
      if (ssrVid) {
        this._lastVariantId = String(ssrVid);
        const ssrAllowed = this._variantPlanMap[this._lastVariantId]
          || this._variantPlanMap._universal || [];
        this._lastHadPlans = ssrAllowed.length > 0;
      }
    }
  }

  // ------------------------------------------------------------------
  //  Scoped variant resolution
  //  On classic PDPs there is one global `#selected-variant-id`. On
  //  multi-product pages (bundle builder) each step has its own scoped
  //  hidden input (`data-default-option`) inside a `[data-bb-step]`
  //  wrapper — the subscription block must read ITS step's variant,
  //  never the first one on the page.
  // ------------------------------------------------------------------

  _getScopedVariantInput() {
    const step = this.blockEl.closest('[data-bb-step]');
    if (step) {
      return step.querySelector('input[data-default-option]');
    }
    return document.getElementById('selected-variant-id');
  }

  /**
   * True when `el` is the variant input this block should react to.
   * Matches the global ids on classic PDPs and the step-scoped input
   * inside bundle builder steps.
   */
  _isOwnVariantInput(el) {
    if (!el) return false;
    const step = this.blockEl.closest('[data-bb-step]');
    if (step) {
      return el.hasAttribute && el.hasAttribute('data-default-option') &&
        el.closest('[data-bb-step]') === step;
    }
    return el.id === 'selected-variant-id' ||
      (el.matches && el.matches('input[name="id"]'));
  }

  // ------------------------------------------------------------------
  //  Selection State
  // ------------------------------------------------------------------

  getFieldset() {
    return this.blockEl.querySelector('fieldset') || this.blockEl.querySelector('.global-product__plan');
  }

  getCheckedInput() {
    if (this.isSellingPlanOnly) {
      return this.blockEl.querySelector('input[name="selling_plan_option"]:checked');
    }
    return this.blockEl.querySelector('input[name="subscription-plan"]:checked');
  }

  isSubscriptionChecked() {
    const checked = this.getCheckedInput();
    const primarySub = checked && !checked.classList.contains('no-subscription-input') &&
      !checked.classList.contains('subscription-upsell-option-input') &&
      !(this.isSellingPlanOnly && checked.id === this.blockId + '-buy_once');
    if (primarySub) return true;
    if (this.hasAdditionalOffer) {
      const addlInput = this.blockEl.querySelector('[data-additional-sub-input]');
      if (addlInput && addlInput.checked) return true;
    }
    return false;
  }

  updateSelectionState() {
    const fieldset = this.getFieldset();
    if (!fieldset) return;

    const hasSelection = this.getCheckedInput();
    if (hasSelection) {
      fieldset.setAttribute('data-sticky-selection', 'selected');
      fieldset.setAttribute('data-selection', 'selected');
      fieldset.classList.remove('not-loaded');
      this.blockEl.setAttribute('data-sticky-selection', 'selected');
      this.blockEl.setAttribute('data-selection', 'selected');
    } else {
      fieldset.removeAttribute('data-sticky-selection');
      fieldset.removeAttribute('data-selection');
      fieldset.setAttribute('data-sticky-selection', '');
      fieldset.classList.remove('not-loaded');
    }

    this.updateAtcText();

    // Re-evaluate the main ATC button's disabled state BEFORE refreshing
    // the floating ATC. `outOfStockHandler(false)` now also gates on
    // whether any subscription block has a checked option — when this
    // method is called from a radio-change handler the subscription was
    // just (de)selected, so `outOfStockHandler` needs to run NOW or
    // `updateFloatingATC` reads a stale `mainATCDisabled` and keeps the
    // floating button locked on the "Choose your subscription" prompt
    // until the next variant change. `outOfStockHandler(false)` itself
    // calls `updateFloatingATC` at the end of its non-event branch, so
    // we no longer need a separate `updateFloatingATC()` invocation
    // here.
    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.outOfStockHandler) {
      gp.outOfStockHandler(false);
    } else if (gp?.updateFloatingATC) {
      gp.updateFloatingATC();
    }

    this.updatePromoStripVisibility();
  }

  updatePromoStripVisibility() {
    const hide = this.isSubscriptionChecked();
    const applyTo = (strip) => {
      strip.classList.toggle('subscription-promo-strip--sub-hidden', hide);
      strip.setAttribute('aria-hidden', hide ? 'true' : 'false');
    };

    // 1. Strips used as a direct child block of this subscription block.
    this.blockEl
      .querySelectorAll('.subscription-promo-strip[data-hide-when-subscribed="true"]')
      .forEach(applyTo);

    // 2. Strips placed elsewhere in the same bundle step / step group (e.g. a
    //    bb_product or bb_step_group child) that opt to follow the subscription
    //    selection. Skip any strip owned by a *different* subscription block so
    //    two blocks never fight over the same strip.
    const scope =
      this.blockEl.closest('[data-bb-step-group]') ||
      this.blockEl.closest('[data-bb-step]');
    if (!scope) return;

    scope
      .querySelectorAll('.subscription-promo-strip[data-hide-when-subscribed="true"]')
      .forEach((strip) => {
        if (this.blockEl.contains(strip)) return;
        const ownSub = strip.closest('.global-product__global-subscription');
        if (ownSub && ownSub !== this.blockEl) return;
        applyTo(strip);
      });
  }

  // ------------------------------------------------------------------
  //  ATC Button Text
  // ------------------------------------------------------------------

  applyAtcLabel(label) {
    const atcBtn = document.querySelector('[data-product-atc]');
    if (!atcBtn) return;
    const next = String(label || '');
    // Skip no-op writes — colour/plan sync was rewriting the same label
    // several times per click and making the ATC text flicker.
    if (
      atcBtn.getAttribute('data-label-add-to-cart') === next &&
      (atcBtn.textContent || '').trim() === next
    ) {
      return;
    }
    atcBtn.setAttribute('data-label-add-to-cart', next);
    atcBtn.textContent = next;

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp) {
      if (gp.buttonLabels) {
        gp.buttonLabels.addToCart = next;
        gp.buttonLabels.floatingAddToCart = next;
      }
      if (gp.updateFloatingATC) gp.updateFloatingATC();
    }
  }

  updateAtcText() {
    if (!this.subscribeAtcText) return;

    const atcBtn = document.querySelector('[data-product-atc]');
    if (!atcBtn) return;

    if (this.originalAtcLabel === null) {
      this.originalAtcLabel = atcBtn.getAttribute('data-label-add-to-cart') || atcBtn.textContent.trim();
    }

    const label = this.isSubscriptionChecked() ? this.subscribeAtcText : this.originalAtcLabel;
    this.applyAtcLabel(label);
  }

  // ------------------------------------------------------------------
  //  Main Product Skip (add-item / replace mode)
  // ------------------------------------------------------------------

  updateMainProductSkip() {
    if (this.isSellingPlanOnly) return;

    const subProduct = this.blockEl.querySelector('product[data-replaces-main]');
    if (!subProduct) return;

    const parent = this.blockEl.closest('.global-product');
    if (!parent) return;

    const mainProduct = parent.querySelector('product[data-variant-picker]');
    if (!mainProduct) return;

    if (this.isSubscriptionChecked()) {
      mainProduct.dataset.skipForSubscription = 'true';
      delete subProduct.dataset.skipForSubscription;
    } else {
      delete mainProduct.dataset.skipForSubscription;
    }
  }

  // ------------------------------------------------------------------
  //  Selling Plan ATC (selling_plan_only mode)
  // ------------------------------------------------------------------

  updateSellingPlanOnATC() {
    if (!this.isSellingPlanOnly) return;

    const buyPlan = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
    const dropdown = this.blockEl.querySelector('select[name="selling_plan_select"]');
    const sellingPlanInput = this.blockEl.querySelector('input[name="selling_plan"]');
    const form = this.blockEl.closest('form');
    const qtyInput = form ? form.querySelector('input[name="quantity"]') : null;
    const atcButtons = document.querySelectorAll('[data-product-atc]');
    let sellingPlanId = '';
    let planQty = '1';
    // Fixed Price Selling Plan: the cart adds a single flat-priced line (planQty 1)
    // but the plan represents a multi-unit pack. `packQty` carries that pack size so
    // the cart badge can still read "Pack of N" even though the line quantity is 1.
    let packQty = '';
    // Display Only Quantity from plan metaobject. Drives the cart "Pack of N"
    // badge only — never multiplies quantity or price.
    let displayPackQty = '';
    // Target Subscription Variant: the plan subscribes to a DIFFERENT product's
    // variant (flavour-matched via the plan's variant link). When set, the ATC
    // swaps the main line's variant id for this one.
    let targetVariantId = '';

    if (buyPlan && buyPlan.checked) {
      if (dropdown) {
        let selectedOpt = dropdown.options[dropdown.selectedIndex];
        if (selectedOpt && (selectedOpt.hidden || selectedOpt.disabled)) {
          selectedOpt = Array.from(dropdown.options).find(o => !o.hidden && !o.disabled) || null;
          if (selectedOpt) dropdown.value = selectedOpt.value;
        }
        sellingPlanId = selectedOpt ? (selectedOpt.dataset.sellingPlanId || '') : '';
        planQty = selectedOpt ? (selectedOpt.dataset.quantity || '1') : '1';
        packQty = selectedOpt ? (selectedOpt.dataset.packQuantity || '') : '';
        displayPackQty = selectedOpt ? (selectedOpt.dataset.displayPackQuantity || '') : '';
        targetVariantId = selectedOpt ? (selectedOpt.dataset.targetVariantId || '') : '';
      } else {
        // Single-plan SPO mode: no dropdown is rendered (`main_plan_count == 1`),
        // and there's no `subscription-plan` radio in SPO mode either (that's an
        // `add_item`-mode construct). Pull the plan's quantity off the buy_plan
        // radio itself — Liquid emits `data-plan-quantity` precisely for this
        // fallback path so e.g. a metaobject `quantity: 3` is honoured.
        sellingPlanId = this.blockEl.dataset.firstSellingPlanId || '';
        const subRadio = this.blockEl.querySelector('input[name="subscription-plan"]');
        // Prefer the SELECTED variant's own plan quantity. With per-variant plans
        // (variant-plan-links), an already-multi-unit SKU (e.g. a "3 Pack") links
        // to a qty-1 plan while single units link to a qty-N plan — the headline
        // price element for each variant carries that quantity. Reading it here
        // keeps the cart quantity in step with the displayed price instead of
        // always using the SSR/first plan's quantity.
        const _scopedVid = (this._getScopedVariantInput() || {}).value;
        const _variantPriceEl = _scopedVid
          ? this.blockEl.querySelector('.price--headline[data-variant-id="' + _scopedVid + '"]')
          : null;
        if (_variantPriceEl && _variantPriceEl.dataset.planQuantity) {
          planQty = _variantPriceEl.dataset.planQuantity;
          packQty = _variantPriceEl.dataset.packQuantity || '';
          displayPackQty = _variantPriceEl.dataset.displayPackQuantity || '';
          targetVariantId = _variantPriceEl.dataset.targetVariantId || '';
        } else if (subRadio && subRadio.dataset.quantity) {
          planQty = subRadio.dataset.quantity.split(',')[0] || '1';
        } else if (buyPlan.dataset.planQuantity) {
          planQty = buyPlan.dataset.planQuantity;
          packQty = buyPlan.dataset.packQuantity || '';
          displayPackQty = buyPlan.dataset.displayPackQuantity || '';
          targetVariantId = buyPlan.dataset.targetVariantId || '';
        }
      }
      if (sellingPlanInput) sellingPlanInput.value = sellingPlanId;
      if (qtyInput) qtyInput.value = planQty;
    } else {
      if (sellingPlanInput) sellingPlanInput.value = '';
      // "Buy once" honours the buy-once quantity selector (defaults to 1).
      planQty = String(this._getBuyOnceQuantity());
      if (qtyInput) qtyInput.value = planQty;
    }

    atcButtons.forEach(btn => btn.setAttribute('data-selling-plan', sellingPlanId));

    // Propagate the resolved plan quantity onto the main variant input so the
    // global ATC iteration (which reads `variant.dataset.quantity` per item)
    // adds N refills instead of 1 when a subscription is active. We stash the
    // original `data-quantity` once so we can restore it when the user toggles
    // back to "Buy once".
    this._syncMainVariantQuantity(planQty, buyPlan && buyPlan.checked, packQty, targetVariantId, displayPackQty);
  }

  /**
   * Writes the active plan quantity onto `#selected-variant-id`'s
   * `data-quantity` attribute. Preserves the original (forced_quantity-
   * derived) value in `data-base-quantity` so toggling sub off restores it.
   */
  _syncMainVariantQuantity(planQty, isSubscriptionActive, packQty, targetVariantId, displayPackQty) {
    const mainVariantInput = this._getScopedVariantInput();
    if (!mainVariantInput) return;

    if (!mainVariantInput.hasAttribute('data-base-quantity')) {
      mainVariantInput.setAttribute(
        'data-base-quantity',
        mainVariantInput.getAttribute('data-quantity') || '1'
      );
    }

    // Fixed-price pack: stash the pack size on the variant input so the ATC loop can
    // attach a `_pack_quantity` line property (cart badge "Pack of N"). Cleared when the
    // active plan isn't a fixed-price pack so normal lines fall back to line quantity.
    if (isSubscriptionActive && packQty && parseInt(packQty, 10) > 1) {
      mainVariantInput.setAttribute('data-pack-quantity', String(packQty));
    } else {
      mainVariantInput.removeAttribute('data-pack-quantity');
    }

    // Display Only Quantity: cosmetic pack size for the cart badge only. Carried
    // separately from data-pack-quantity so it never feeds any price math.
    if (isSubscriptionActive && displayPackQty && parseInt(displayPackQty, 10) > 1) {
      mainVariantInput.setAttribute('data-display-pack-quantity', String(displayPackQty));
    } else {
      mainVariantInput.removeAttribute('data-display-pack-quantity');
    }

    // Target Subscription Variant: stash the redirect target so the ATC loop adds
    // THAT variant (a different product, flavour-matched via the plan link) with
    // the selling plan instead of the page variant. Cleared on "Buy once" so the
    // one-time purchase still adds the page variant.
    if (isSubscriptionActive && targetVariantId) {
      mainVariantInput.setAttribute('data-sub-target-variant', String(targetVariantId));
    } else {
      mainVariantInput.removeAttribute('data-sub-target-variant');
    }

    if (isSubscriptionActive) {
      mainVariantInput.setAttribute('data-quantity', String(planQty || '1'));
    } else {
      // "Buy once": when the buy-once quantity selector is above 1, honour it.
      // Otherwise restore the original (forced_quantity-derived) base value.
      const buyOnceQty = this._getBuyOnceQuantity();
      if (buyOnceQty > 1) {
        mainVariantInput.setAttribute('data-quantity', String(buyOnceQty));
      } else {
        mainVariantInput.setAttribute(
          'data-quantity',
          mainVariantInput.getAttribute('data-base-quantity') || '1'
        );
      }
    }
  }

  // ------------------------------------------------------------------
  //  Buy-once quantity selector
  //  On the bundle builder this is handled by BundleBuilder.js; on
  //  classic PDPs the global product controller owns it. The control is
  //  rendered inside the "Buy once" radio's <label>.
  // ------------------------------------------------------------------

  /**
   * Returns the buy-once quantity control that is currently visible.
   * Several layout branches can render one in the DOM, but only one is
   * shown at a time.
   */
  _getActiveBuyOnceControl() {
    const controls = this.blockEl.querySelectorAll('[data-subscription-buy-once-qty]');
    if (controls.length <= 1) return controls[0] || null;
    for (let i = 0; i < controls.length; i++) {
      if (controls[i].offsetParent !== null) return controls[i];
    }
    return controls[0];
  }

  _getBuyOnceQuantity() {
    const control = this._getActiveBuyOnceControl();
    if (!control) return 1;
    const valueEl = control.querySelector('[data-buy-once-qty-value]');
    const v = parseInt((valueEl && valueEl.textContent) || '1', 10);
    return Number.isNaN(v) || v < 1 ? 1 : v;
  }

  _bindBuyOnceQuantities() {
    // Inside the bundle builder these steppers are owned by BundleBuilder.js;
    // binding here too would double-count each click.
    if (this.blockEl.closest('[data-bb-step]') || this.blockEl.closest('[data-bundle-builder]')) {
      return;
    }

    this.blockEl.querySelectorAll('[data-subscription-buy-once-qty]').forEach((control) => {
      const valueEl = control.querySelector('[data-buy-once-qty-value]');
      const max = parseInt(control.dataset.max || '5', 10) || 5;
      let value = parseInt((valueEl && valueEl.textContent) || '1', 10) || 1;

      const sync = () => {
        value = Math.min(max, Math.max(1, value));
        if (valueEl) valueEl.textContent = String(value);

        // Selecting a quantity implies the "Buy once" option.
        const label = control.closest('label');
        const input = label && label.htmlFor
          ? document.getElementById(label.htmlFor)
          : null;
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this._applyBuyOnceQuantity();
      };

      const decrease = control.querySelector('[data-buy-once-qty-decrease]');
      if (decrease) {
        const handler = (event) => {
          event.preventDefault();
          event.stopPropagation();
          value -= 1;
          sync();
        };
        decrease.addEventListener('click', handler);
        this._cleanup.push(() => decrease.removeEventListener('click', handler));
      }

      const increase = control.querySelector('[data-buy-once-qty-increase]');
      if (increase) {
        const handler = (event) => {
          event.preventDefault();
          event.stopPropagation();
          value += 1;
          sync();
        };
        increase.addEventListener('click', handler);
        this._cleanup.push(() => increase.removeEventListener('click', handler));
      }
    });
  }

  /**
   * Pushes the current buy-once quantity into the ATC plumbing and refreshes
   * pricing/selection state, mirroring what a radio change does.
   */
  _applyBuyOnceQuantity() {
    if (this.isSellingPlanOnly) {
      this.updateSellingPlanOnATC();
    } else {
      // add_item mode: the "Buy once" radio adds the main product variant, so
      // propagate the quantity onto the scoped variant input directly.
      this._syncMainVariantQuantity('1', false);
    }

    this.updateSelectionState();
    this.syncOneTimePurchasePrice();

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.updatePricing) {
      gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
    }
  }

  // ------------------------------------------------------------------
  //  Price Display
  // ------------------------------------------------------------------

  updateVisibleVariantPrices(variantId) {
    if (!variantId) return;
    const vid = String(variantId);
    this.blockEl.querySelectorAll(
      '.price[data-variant-id], [data-plan-row-price] [data-variant-id], [data-otp-breakdown-c2-price] [data-variant-id]'
    ).forEach(el => {
      el.style.display = el.dataset.variantId === vid ? '' : 'none';
    });
    this.syncCompactPriceInPlanRow();
    this.syncSimplePlanSubtitle();
    this.updatePricePerDay();
  }

  /**
   * Sync OTP breakdown component-1 (kit upsell / bottle) price + visibility
   * from the currently checked kit-selection ATC input.
   */
  /**
   * OTP breakdown is buy-once pricing — always use the upsell's full
   * (pre-subscription-discount) price so switching to Subscribe does not
   * rewrite Bottle / component rows.
   */
  _getOtpBreakdownUpsellPriceCents(upsellInput) {
    if (!upsellInput) return 0;
    const full =
      Number(upsellInput.getAttribute('data-upsell-full-price')) ||
      Number(upsellInput.dataset.upsellFullPrice) ||
      0;
    if (full > 0) return full;
    // No subscription-discount metadata — current ATC price is the OTP amount.
    return Number(upsellInput.dataset.price) || 0;
  }

  _isOtpBreakdownSequencingReady() {
    if (this.blockEl.dataset.progressiveDisclosure !== 'true') return true;
    // Only show once preceding conditional steps have unlocked this block.
    return this.blockEl.dataset.locked === 'false';
  }

  _isOtpBreakdownFeaturedVariant(breakdown, variantId) {
    if (!breakdown || breakdown.dataset.otpFeaturedEnabled !== 'true') return false;
    const ids = (breakdown.dataset.otpFeaturedVariantIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
    if (!ids.length || !variantId) return false;
    return ids.includes(String(variantId));
  }

  _syncOtpBreakdownFeaturedLabels(breakdown, variantId) {
    if (!breakdown || breakdown.dataset.otpFeaturedEnabled !== 'true') return;

    const featured = this._isOtpBreakdownFeaturedVariant(breakdown, variantId);
    const c2Label = featured
      ? (breakdown.dataset.otpC2LabelFeatured || breakdown.dataset.otpC2LabelDefault || '')
      : (breakdown.dataset.otpC2LabelDefault || '');
    const c2Brackets = featured
      ? (breakdown.dataset.otpC2BracketsFeatured || '')
      : (breakdown.dataset.otpC2BracketsDefault || '');
    const qtyLabel = featured
      ? (breakdown.dataset.otpQtyLabelFeatured || breakdown.dataset.otpQtyLabelDefault || '')
      : (breakdown.dataset.otpQtyLabelDefault || '');

    const c2LabelEl = breakdown.querySelector('[data-otp-c2-label-text]');
    if (c2LabelEl) c2LabelEl.textContent = c2Label;

    const c2BracketsEl = breakdown.querySelector('[data-otp-c2-brackets-text]');
    if (c2BracketsEl) {
      if (c2Brackets) {
        c2BracketsEl.textContent = '(' + c2Brackets + ')';
        c2BracketsEl.hidden = false;
      } else {
        c2BracketsEl.textContent = '';
        c2BracketsEl.hidden = true;
      }
    }

    const qtyLabelEl = breakdown.querySelector('[data-otp-qty-label-text]');
    if (qtyLabelEl) qtyLabelEl.textContent = qtyLabel;
  }

  /**
   * Whether this subscription block is the intended target of a kit upsell
   * discount's optional block-id filter. Matches merchant identifiers
   * (`data-subscription-block-id`, `data-bb-subscription-id`) — not only the
   * generated DOM id — and falls back like `isSubscriptionSelected` when the
   * configured id isn't present on any block.
   */
  _matchesUpsellDiscountBlockFilter(blockIdFilter) {
    if (!blockIdFilter) return true;

    const ownIds = [
      this.blockEl.dataset.subscriptionBlockId,
      this.blockEl.dataset.bbSubscriptionId,
      this.blockId,
    ].filter(Boolean);

    if (ownIds.includes(blockIdFilter)) return true;

    // Configured id exists on another block → this one is not the target.
    const targetExists = !!document.querySelector(
      `[data-subscription-block-id="${blockIdFilter}"], [data-bb-subscription-id="${blockIdFilter}"]`
    );
    if (targetExists) return false;

    // Configured id not found on the page — don't silently fail.
    return true;
  }

  /**
   * Forest-green badge under Subscribe label: "Free bottle" / "Save £X on bottle".
   * Driven by the kit selector's upsell discount (fixed amount or percentage),
   * or a bundle-builder product step with a matching display-only bottle discount.
   */
  syncSubscribeUpsellDiscountBadge() {
    if (this.blockEl.dataset.upsellDiscountBadge !== 'true') return;

    const badges = this.blockEl.querySelectorAll('[data-subscribe-upsell-discount-badge]');
    if (!badges.length) return;

    const template =
      this.blockEl.dataset.upsellDiscountBadgeTemplate || 'Save [amount] on bottle';

    const hideBadges = () => {
      badges.forEach((badge) => {
        badge.hidden = true;
        badge.textContent = '';
      });
    };

    let value = this._resolveSubscribeUpsellDiscountValue();
    if (!(value > 0)) {
      hideBadges();
      return;
    }

    const moneyFormat =
      document.body.getAttribute('data-money-format') || '${{amount}}';
    const amount = Shopify.formatMoney(Math.round(value * 100), moneyFormat);
    const text = template.replace(/\[amount\]/gi, amount);

    badges.forEach((badge) => {
      badge.textContent = text;
      badge.hidden = false;
    });
  }

  /**
   * Bottle discount amount (major currency units) for the Subscribe badge.
   * Prefers an active kit upsell ATC input, then any matching kit upsell,
   * then a bundle-builder step with data-bb-discount-type="fixed".
   * Percentage discounts are converted using the upsell's full price.
   */
  _upsellDiscountAmount(input) {
    if (!input) return 0;
    const type = input.getAttribute('data-upsell-discount-type');
    const raw = parseFloat(input.getAttribute('data-upsell-discount-value')) || 0;
    if (!(raw > 0)) return 0;
    if (type === 'fixed') return raw;
    if (type === 'percentage') {
      const fullCents = Number(input.getAttribute('data-upsell-full-price')) || 0;
      return (fullCents * raw) / 10000;
    }
    return 0;
  }

  _resolveSubscribeUpsellDiscountValue() {
    let upsell = document.querySelector(
      'input[data-upsell-atc-input]:checked[data-upsell-discount-type]'
    );
    if (upsell && !(this._upsellDiscountAmount(upsell) > 0)) upsell = null;

    if (!upsell) {
      const candidates = document.querySelectorAll(
        'input[data-upsell-atc-input][data-upsell-discount-type]'
      );
      for (const candidate of candidates) {
        const filter =
          candidate.getAttribute('data-upsell-discount-subscription-block-id') || '';
        if (
          this._matchesUpsellDiscountBlockFilter(filter) &&
          this._upsellDiscountAmount(candidate) > 0
        ) {
          upsell = candidate;
          break;
        }
      }
    }

    if (upsell) {
      const blockIdFilter =
        upsell.getAttribute('data-upsell-discount-subscription-block-id') || '';
      if (this._matchesUpsellDiscountBlockFilter(blockIdFilter)) {
        const kitValue = this._upsellDiscountAmount(upsell);
        if (kitValue > 0) return kitValue;
      }
    }

    const root = this.blockEl.closest('[data-bundle-builder]') || document;
    const steps = root.querySelectorAll(
      '[data-bb-step][data-bb-discount-type="fixed"]'
    );
    for (const step of steps) {
      const filter = step.dataset.bbDiscountSubscriptionId || '';
      if (!this._matchesUpsellDiscountBlockFilter(filter)) continue;
      const stepValue = parseFloat(step.dataset.bbDiscountValue || '0') || 0;
      if (stepValue > 0) return stepValue;
    }

    return 0;
  }

  syncOtpBreakdown() {
    if (this.blockEl.dataset.otpBreakdown !== 'true') return;

    const sequencingReady = this._isOtpBreakdownSequencingReady();
    const moneyFormat = document.body.getAttribute('data-money-format') || '${{amount}}';
    const checkedUpsell = document.querySelector('input[data-upsell-atc-input]:checked');
    const priceCents = this._getOtpBreakdownUpsellPriceCents(checkedUpsell);
    const hasComponent1 = Boolean(checkedUpsell && priceCents > 0);
    const formatted = hasComponent1 ? Shopify.formatMoney(priceCents, moneyFormat) : '';
    const variantId = this._getCurrentVariantId();

    this.blockEl.querySelectorAll('[data-otp-breakdown]').forEach(breakdown => {
      breakdown.hidden = !sequencingReady;
      this._syncOtpBreakdownFeaturedLabels(breakdown, variantId);

      const row = breakdown.querySelector('[data-otp-breakdown-component="1"]');
      const priceEl = breakdown.querySelector('[data-otp-breakdown-c1-price]');
      if (!row || !priceEl) return;
      if (hasComponent1) {
        priceEl.textContent = formatted;
        row.hidden = false;
      } else {
        priceEl.textContent = '';
        row.hidden = true;
      }
    });
  }

  updateAddItemBoxPrice() {
    if (this.isSellingPlanOnly) return;
    let activeSubInput = null;
    if (this.useColorMatch) {
      const activeSubSet = this.blockEl.querySelector('.sub-colour-set.active');
      if (activeSubSet) activeSubInput = activeSubSet.querySelector('input[name="subscription-plan"]');
    } else {
      activeSubInput = this.blockEl.querySelector('input[name="subscription-plan"]:not(.no-subscription-input)');
    }
    if (!activeSubInput) return;

    const priceVal = Number(activeSubInput.dataset.price || 0);
    const compareVal = Number(activeSubInput.dataset.comparePrice || 0);
    const qtyStr = activeSubInput.dataset.quantity || '1';
    const qty = Number(qtyStr.split(',')[0]) || 1;
    const mainPrice = priceVal * qty;
    const mainCompare = compareVal * qty;
    let totalPrice = mainPrice;
    let totalCompare = mainCompare;

    const comboPrice = Number(activeSubInput.dataset.comboPrice || 0);
    const comboCompare = Number(activeSubInput.dataset.comboCompare || comboPrice);
    const comboQty = Number(activeSubInput.dataset.comboQty || 0);
    let comboTotalPrice = 0;
    let comboTotalCompare = 0;
    if (comboPrice > 0 && comboQty > 0) {
      comboTotalPrice = comboPrice * comboQty;
      comboTotalCompare = comboCompare * comboQty;
      totalPrice += comboTotalPrice;
      totalCompare += comboTotalCompare;
    }
    const moneyFormat = document.body.getAttribute('data-money-format') || '${{amount}}';

    const label = activeSubInput.closest('.sub-colour-set') || activeSubInput.parentElement;
    if (!label) return;
    const priceDiv = label.querySelector('.price') || (activeSubInput.nextElementSibling && activeSubInput.nextElementSibling.querySelector('.price'));
    if (!priceDiv) return;

    let totalHtml = Shopify.formatMoney(totalPrice, moneyFormat);
    if (totalCompare > totalPrice) {
      totalHtml += ' <span class="compare-at-price">' + Shopify.formatMoney(totalCompare, moneyFormat) + '</span>';
    }
    priceDiv.innerHTML = totalHtml;

    let mainHtml = Shopify.formatMoney(mainPrice, moneyFormat);
    if (mainCompare > mainPrice) {
      mainHtml += ' <span class="compare-at-price">' + Shopify.formatMoney(mainCompare, moneyFormat) + '</span>';
    }

    const deliveryColPrice = label.querySelector('[data-delivery-col-price]');
    if (deliveryColPrice) {
      deliveryColPrice.innerHTML = (comboTotalPrice > 0 ? mainHtml : totalHtml);
    }

    this.blockEl.querySelectorAll('[data-plan-row-price]').forEach(rowSlot => {
      rowSlot.innerHTML = '<span>' + mainHtml + '</span>';
    });

    if (comboTotalPrice > 0) {
      let comboHtml = Shopify.formatMoney(comboTotalPrice, moneyFormat);
      if (comboTotalCompare > comboTotalPrice) {
        comboHtml += ' <span class="compare-at-price">' + Shopify.formatMoney(comboTotalCompare, moneyFormat) + '</span>';
      }
      this.blockEl.querySelectorAll('[data-combo-row-price]').forEach(rowSlot => {
        rowSlot.innerHTML = '<span>' + comboHtml + '</span>';
      });
      const comboDeliveryPrice = this.blockEl.querySelector('[data-combo-delivery-col-price]');
      if (comboDeliveryPrice) {
        comboDeliveryPrice.innerHTML = comboHtml;
      }
    }

    this.syncCompactPriceInPlanRow();
    this.syncSimplePlanSubtitle();
    this.updatePricePerDay();
  }

  syncCompactPriceInPlanRow() {
    if ((this.blockEl.dataset.layout || '') !== 'compact') return;

    const activeVariantId = this._getCurrentVariantId();

    this.blockEl.querySelectorAll('[data-plan-row-price]').forEach(slot => {
      const variantSpans = slot.querySelectorAll('[data-variant-id]');
      if (variantSpans.length > 0 && activeVariantId) {
        variantSpans.forEach(span => {
          span.style.display = span.dataset.variantId === String(activeVariantId) ? '' : 'none';
        });
      }
    });
  }

  syncSimplePlanSubtitle() {
    if ((this.blockEl.dataset.layout || '') !== 'simple') return;

    this.blockEl.querySelectorAll('[data-plan-subtitle]').forEach(subtitleEl => {
      const label = subtitleEl.closest('label');
      if (!label) return;

      const visibleRow = label.querySelector('[data-plan-thumbnails] > div:not([style*="display: none"]):not([style*="display:none"])');
      if (!visibleRow) return;

      const parts = [];
      visibleRow.querySelectorAll('p').forEach(p => {
        const text = (p.textContent || '').trim();
        if (text) parts.push(text);
      });
      subtitleEl.textContent = parts.join(' ');
    });
  }

  updatePricePerDay() {
    this.blockEl.querySelectorAll('[data-show-price-per-day]').forEach(columns => {
      const perDayEl = columns.querySelector('[data-price-per-day]');
      if (!perDayEl) return;

      const isCombo = columns.hasAttribute('data-combo-delivery-columns');
      const intervalEl = isCombo
        ? columns.querySelector('[data-combo-delivery-col-value]')
        : columns.querySelector('[data-delivery-col-value]');
      const intervalText = (intervalEl?.textContent || '').trim().toLowerCase();
      const days = this._intervalToDays(intervalText);
      if (!days) { perDayEl.textContent = ''; return; }

      let priceCents = 0;
      const colPrice = isCombo
        ? columns.querySelector('[data-combo-delivery-col-price]')
        : columns.querySelector('[data-delivery-col-price]');
      if (colPrice) {
        priceCents = this._extractPriceCents(colPrice);
      } else {
        const visiblePrice = columns.querySelector('.price[data-variant-id]:not([style*="display: none"]):not([style*="display:none"])');
        if (visiblePrice) priceCents = this._extractPriceCents(visiblePrice);
      }

      if (!priceCents) { perDayEl.textContent = ''; return; }

      const perDay = priceCents / days;
      const moneyFormat = document.body.getAttribute('data-money-format') || '${{amount}}';
      perDayEl.textContent = '(' + Shopify.formatMoney(Math.round(perDay), moneyFormat) + '/day)';
    });
  }

  _intervalToDays(text) {
    const match = text.match(/every\s+(\d+)\s+(day|week|month|year)s?/i);
    if (!match) return 0;
    const count = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();
    if (unit === 'day') return count;
    if (unit === 'week') return count * 7;
    if (unit === 'month') return count * 30;
    if (unit === 'year') return count * 365;
    return 0;
  }

  _extractPriceCents(el) {
    const clone = el.cloneNode(true);
    const compareAt = clone.querySelector('.compare-at-price');
    if (compareAt) compareAt.remove();
    const text = clone.textContent.replace(/[^0-9.,]/g, '').trim();
    const num = parseFloat(text.replace(',', ''));
    if (isNaN(num)) return 0;
    return Math.round(num * 100);
  }

  syncOneTimePurchasePrice() {
    const vid = this._getCurrentVariantId();
    if (vid) this.updateVisibleVariantPrices(vid);
    this.syncOtpBreakdown();
    this.syncSubscribeUpsellDiscountBadge();
  }

  // ------------------------------------------------------------------
  //  Color Sync
  // ------------------------------------------------------------------

  onColorChanged(newColorHandle, variantId) {
    if (!this.useColorMatch) return;

    const vid = variantId != null && variantId !== '' ? String(variantId) : '';
    const colorHandle =
      newColorHandle || (vid && this.variantColorMap[vid]) || '';

    // Derive preference before mutating — needed to know which radio to target.
    let preference = window.subscriptionPreference;
    if (!preference) {
      const currentlyChecked = this.getCheckedInput();
      if (currentlyChecked) {
        preference = currentlyChecked.classList.contains('no-subscription-input')
          ? 'no-subscription'
          : 'subscription';
      } else if (this.defaultSelection === 'none') {
        // Still refresh active colour-set chrome, but don't force a plan pick.
        this._syncColourSetActiveClasses(colorHandle, vid);
        return;
      } else {
        preference =
          this.defaultSelection === 'no_subscription'
            ? 'no-subscription'
            : 'subscription';
      }
    }

    let newInput = null;
    if (preference === 'no-subscription') {
      const noSubSet = vid
        ? this.blockEl.querySelector(
            '.no-sub-colour-set[data-variant-id="' + vid + '"]'
          )
        : this.blockEl.querySelector(
            '.no-sub-colour-set[data-colour-swap="' + colorHandle + '"]'
          );
      newInput = noSubSet
        ? noSubSet.querySelector('input.no-subscription-input')
        : this.blockEl.querySelector('.no-subscription-input');
    } else {
      const subSet = this.blockEl.querySelector(
        '.sub-colour-set[data-colour-swap="' + colorHandle + '"]'
      );
      if (subSet) {
        newInput = subSet.querySelector('input[name="subscription-plan"]');
      }
    }

    const currentChecked = this.getCheckedInput();
    const alreadyCorrect = !!(newInput && newInput.checked && newInput === currentChecked);

    // Always keep the visible colour-set in sync with the main picker.
    this._syncColourSetActiveClasses(colorHandle, vid);

    // Already on the right plan radio for this colour — do not re-dispatch
    // change (that was thrashing ATC label / OOS handlers on every swatch).
    if (alreadyCorrect || !newInput || newInput.disabled) return;

    newInput.checked = true;
    if (currentChecked && currentChecked !== newInput) {
      currentChecked.checked = false;
    }
    this.updateMainProductSkip();
    // Dispatch once — the radio change handler runs updateSelectionState /
    // pricing / OOS. Avoid calling those again here.
    newInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _syncColourSetActiveClasses(colorHandle, variantId) {
    const vid = variantId != null && variantId !== '' ? String(variantId) : '';
    this.blockEl.querySelectorAll('.no-sub-colour-set.colour').forEach((el) => {
      if (vid && el.dataset.variantId) {
        el.classList.toggle('active', el.dataset.variantId === vid);
      } else {
        el.classList.toggle('active', el.dataset.colourSwap === colorHandle);
      }
    });
    this.blockEl.querySelectorAll('.sub-colour-set.colour').forEach((el) => {
      el.classList.toggle(
        'active',
        !!colorHandle && el.dataset.colourSwap === colorHandle
      );
    });
  }

  syncSelectionToCurrentVariantColor() {
    if (!this.useColorMatch) return;
    const step = this.blockEl.closest('[data-bb-step]');
    const variantIdInput = step ? null : document.querySelector('input[name="id"]');
    const selectedVariantInput = this._getScopedVariantInput();
    const activeVariantId = (variantIdInput && variantIdInput.value) || (selectedVariantInput && selectedVariantInput.value);
    if (!activeVariantId) return;

    const newColorHandle = this.variantColorMap[String(activeVariantId)];
    // Still sync by variant id even if the colour map misses — activation
    // prefers data-variant-id on the colour-set wrappers.
    this.onColorChanged(newColorHandle || '', activeVariantId);
    if (newColorHandle) this.syncAdditionalOfferColor(newColorHandle);
    this.syncUpsellColor(newColorHandle || '', activeVariantId);
  }

  syncUpsellColor(colorHandle, variantId) {
    const upsellInput = this.blockEl.querySelector(
      '[data-upsell-atc-input].subscription-upsell-option-input'
    ) || this.blockEl.querySelector('[data-upsell-atc-input]');
    if (!upsellInput) return;

    const mapScript =
      document.querySelector('script[data-upsell-match-data="' + this.blockId + '"]') ||
      this.blockEl.querySelector('script[data-upsell-match-data]');
    if (!mapScript) return;

    let data;
    try { data = JSON.parse(mapScript.textContent.trim()); } catch (e) { return; }

    const handleize = (str) => String(str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    let handle = handleize(colorHandle);
    if (!handle && variantId) {
      handle = handleize((data.mainHandleByVariantId || {})[String(variantId)]);
    }
    if (!handle) {
      const selected = this._getScopedVariantInput();
      const mainId = selected && String(selected.value || '').split(',')[0];
      if (mainId) handle = handleize((data.mainHandleByVariantId || {})[mainId]);
    }
    if (!handle) return;

    const target = (data.upsellByHandle || {})[handle];
    if (!target || !target.id) return;

    const targetId = String(target.id);
    if (String(upsellInput.value) === targetId) return;

    const wasChecked = upsellInput.checked;
    upsellInput.value = targetId;
    if (upsellInput.dataset) {
      upsellInput.dataset.variantId = targetId;
      upsellInput.dataset.price = String(target.price || '');
      upsellInput.dataset.comparePrice = String(target.compare_at_price || target.price || '');
      upsellInput.dataset.available = target.available === false ? 'false' : 'true';
    }
    upsellInput.disabled = target.available === false;

    const priceEl = this.blockEl.querySelector('[data-upsell-price]');
    if (priceEl && window.Shopify && Shopify.formatMoney) {
      const moneyFormat = document.body.getAttribute('data-money-format') || '${amount}';
      let html = Shopify.formatMoney(target.price, moneyFormat);
      if (target.compare_at_price && Number(target.compare_at_price) > Number(target.price)) {
        html += ' <span class="compare-at-price">' + Shopify.formatMoney(target.compare_at_price, moneyFormat) + '</span>';
      }
      priceEl.innerHTML = html;
    }

    if (wasChecked) {
      upsellInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  syncAdditionalOfferColor(colorHandle) {
    if (!this.hasAdditionalOffer) return;
    const addlInput = this.blockEl.querySelector('[data-additional-sub-input]');
    if (!addlInput) return;

    const mapScript = this.blockEl.querySelector('script[data-addl-colour-map-json]');
    if (!mapScript) return;

    let colourMap = [];
    try { colourMap = JSON.parse(mapScript.textContent); } catch(e) { return; }
    if (!colourMap.length) return;

    const targetHandle = (colorHandle || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    if (!targetHandle) return;

    const match = colourMap.find(item =>
      item.colour === targetHandle ||
      (item.colour_raw || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === targetHandle
    );
    if (!match) return;

    addlInput.value = String(match.id);
    addlInput.dataset.variantId = String(match.id);
    addlInput.dataset.price = String(match.price);
    addlInput.dataset.comparePrice = String(match.compare_price);
  }

  // ------------------------------------------------------------------
  //  Double Up (Bundle colour-match duplication)
  // ------------------------------------------------------------------

  initDoubleUp() {
    if (this.blockEl.dataset.doubleUpEnabled !== 'true') return;

    const strip = this.blockEl.querySelector('[data-double-up-strip]');
    if (!strip) return;

    const colourOptionGroups = document.querySelectorAll('.product_variant_option');
    let colourGroups = [];
    colourOptionGroups.forEach(group => {
      const optionIndex = group.dataset.optionIndex;
      const hasSwatches = group.querySelector('[data-swapper]') || group.querySelector('.swatch');
      const hasBundleSwatches = group.querySelector('.bundle-swatch-wrapper');
      const hasSwatchOptions = group.querySelector('.swatch__options') || group.querySelector('.colour');
      const titleRow = group.querySelector('.title__row span');
      const titleEl = titleRow || group.querySelector('.title');
      const titleText = titleEl ? titleEl.textContent.toLowerCase().trim() : '';
      const isColour = titleText.includes('colour') || titleText.includes('color') || titleText.includes('toothbrush') || hasSwatches || hasBundleSwatches || hasSwatchOptions;
      if (isColour) colourGroups.push({ group, optionIndex });
    });

    if (colourGroups.length < 2) return;

    strip.style.display = '';
    this.blockEl.setAttribute('data-double-up-visible', 'true');
    this._doubleUpReady = true;
    this._doubleUpStrip = strip;

    // Set initial expanded state based on whether subscription is already checked
    if (this.isSubscriptionChecked()) {
      strip.classList.add('is-expanded');
    }
    this._doubleUpActive = false;
    this._doubleUpSecondGroup = colourGroups[1];
    this._doubleUpInput = strip.querySelector('[data-double-up-input]');

    const toggleBtn = strip.querySelector('[data-double-up-toggle]');
    const activeState = strip.querySelector('[data-double-up-active]');
    const removeBtn = strip.querySelector('[data-double-up-remove]');

    if (toggleBtn) {
      const toggleHandler = () => this.activateDoubleUp();
      toggleBtn.addEventListener('click', toggleHandler);
      this._cleanup.push(() => toggleBtn.removeEventListener('click', toggleHandler));
    }

    if (removeBtn) {
      const removeHandler = () => this.deactivateDoubleUp();
      removeBtn.addEventListener('click', removeHandler);
      this._cleanup.push(() => removeBtn.removeEventListener('click', removeHandler));
    }

    const secondGroupInputHandler = () => {
      if (this._doubleUpActive) {
        setTimeout(() => this.syncDoubleUpVariant(), 50);
      }
    };
    this._doubleUpSecondGroup.group.addEventListener('change', secondGroupInputHandler);
    this._cleanup.push(() => this._doubleUpSecondGroup.group.removeEventListener('change', secondGroupInputHandler));
  }

  toggleDoubleUpVisibility() {
    if (!this._doubleUpReady || !this._doubleUpStrip) return;
    if (this.isSubscriptionChecked()) {
      this._doubleUpStrip.classList.add('is-expanded');
    } else {
      this._doubleUpStrip.classList.remove('is-expanded');
      if (this._doubleUpActive) this.deactivateDoubleUp();
    }
  }

  activateDoubleUp() {
    if (!this._doubleUpStrip || !this._doubleUpInput) return;
    this._doubleUpActive = true;

    const toggleBtn = this._doubleUpStrip.querySelector('[data-double-up-toggle]');
    const activeState = this._doubleUpStrip.querySelector('[data-double-up-active]');
    if (toggleBtn) toggleBtn.style.display = 'none';
    if (activeState) activeState.style.display = '';

    this.syncDoubleUpVariant();
    this._doubleUpInput.checked = true;
    this._doubleUpInput.dispatchEvent(new Event('change', { bubbles: true }));

    this.blockEl.dataset.doubleUpActive = 'true';

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.updatePricing) {
      gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
    }
  }

  deactivateDoubleUp() {
    if (!this._doubleUpStrip || !this._doubleUpInput) return;
    this._doubleUpActive = false;

    const toggleBtn = this._doubleUpStrip.querySelector('[data-double-up-toggle]');
    const activeState = this._doubleUpStrip.querySelector('[data-double-up-active]');
    if (toggleBtn) toggleBtn.style.display = '';
    if (activeState) activeState.style.display = 'none';

    this._doubleUpInput.checked = false;
    this._doubleUpInput.dataset.price = '0';
    this._doubleUpInput.dataset.comparePrice = '0';
    this._doubleUpInput.dataset.variantId = '';
    this._doubleUpInput.value = '';

    this.blockEl.dataset.doubleUpActive = 'false';

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.updatePricing) {
      gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
    }
  }

  syncDoubleUpVariant() {
    if (!this._doubleUpInput || !this._doubleUpSecondGroup) return;

    const checkedInput = this._doubleUpSecondGroup.group.querySelector('input[type="radio"]:checked');
    if (!checkedInput) return;

    const colourHandle = checkedInput.dataset.swapper || checkedInput.dataset.optionValue || checkedInput.value;
    if (!colourHandle) return;

    const normalizedHandle = colourHandle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    const mapScript = this.blockEl.querySelector('script[data-sub-colour-map-json]');
    if (!mapScript) return;

    let colourMap = [];
    try { colourMap = JSON.parse(mapScript.textContent); } catch(e) { return; }

    const match = colourMap.find(item =>
      item.colour === normalizedHandle ||
      (item.colour_raw || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') === normalizedHandle
    );

    if (match) {
      this._doubleUpInput.value = String(match.id);
      this._doubleUpInput.dataset.variantId = String(match.id);
      this._doubleUpInput.dataset.price = String(match.price);
      this._doubleUpInput.dataset.comparePrice = String(match.compare_price);

      const sellingPlan = this.blockEl.dataset.firstSellingPlanId || '';
      this._doubleUpInput.dataset.sellingPlan = sellingPlan;

      const summaryEl = this._doubleUpStrip.querySelector('[data-double-up-summary]');
      if (summaryEl) {
        const moneyFormat = document.body.getAttribute('data-money-format') || '${{amount}}';
        const qty = Number(this._doubleUpInput.dataset.quantity || 1);
        const price = Shopify.formatMoney(match.price * qty, moneyFormat);
        const colourName = match.colour_raw || colourHandle;
        summaryEl.textContent = qty + 'x ' + colourName + ' — ' + price;
      }
    }

    if (this._doubleUpActive) {
      const gp = window.PXUTheme?.jsGlobalProduct;
      if (gp?.updatePricing) {
        gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
      }
    }
  }

  // ------------------------------------------------------------------
  //  Progressive Disclosure
  // ------------------------------------------------------------------

  checkProgressiveDisclosure() {
    if (this.blockEl.dataset.progressiveDisclosure !== 'true') return;

    const variantInput = this._getScopedVariantInput();
    const ownUnselected = !!(
      variantInput && variantInput.hasAttribute('data-options-unselected')
    );

    // In a bundle builder this plan step must stay locked until EVERY earlier
    // step in the global flow (other products' options AND their plans) has a
    // selection — not just its own product's options. Otherwise a later
    // product's plan would be selectable before earlier steps are done.
    if (ownUnselected || this._hasIncompletePrecedingStep()) {
      this.blockEl.dataset.locked = 'true';
    } else {
      this.blockEl.dataset.locked = 'false';
    }

    this.syncOtpBreakdown();
  }

  /**
   * True when any `[data-sticky-selection]` step before this block's own
   * fieldset (in document order, within the bundle builder) is still without a
   * selection. Classic single-product PDPs have no preceding product steps, so
   * this always returns false there and behaviour is unchanged.
   */
  _hasIncompletePrecedingStep() {
    const bundle = this.blockEl.closest('[data-bundle-builder]');
    if (!bundle) return false;

    const stickyEls = Array.from(
      bundle.querySelectorAll('[data-sticky-selection]')
    );
    const ownFieldset = this.getFieldset();
    const ownIndex = ownFieldset ? stickyEls.indexOf(ownFieldset) : -1;

    for (let i = 0; i < stickyEls.length; i++) {
      if (ownIndex !== -1 && i >= ownIndex) break;
      const el = stickyEls[i];
      // Skip steps that belong to this very block (its own fieldset is gated
      // by `ownUnselected`; never gate a block against itself).
      if (this.blockEl.contains(el)) continue;
      const selected =
        el.dataset.stickySelection === 'selected' ||
        el.dataset.selection === 'selected' ||
        el.querySelector('input:checked');
      if (!selected) return true;
    }
    return false;
  }

  /**
   * Re-evaluates the lock and auto-applies the default selection if the block
   * just unlocked. Shared by the variant-input observer and the bundle-wide
   * change listener so the lock reacts to ANY earlier step changing, not only
   * this product's own variant.
   */
  refreshLockState() {
    if (this.blockEl.dataset.progressiveDisclosure !== 'true') return;
    const wasLocked = this.blockEl.dataset.locked === 'true';
    this.checkProgressiveDisclosure();
    const isNowUnlocked = this.blockEl.dataset.locked === 'false';
    if (
      isNowUnlocked &&
      this.defaultSelection !== 'none' &&
      !this.getCheckedInput() &&
      !this._shouldSkipUnlockDefault()
    ) {
      this.applyDefaultSelection();
    } else if (!wasLocked && !isNowUnlocked) {
      this.clearPlanSelection();
    }
  }

  // ------------------------------------------------------------------
  //  Default Selection
  // ------------------------------------------------------------------

  /**
   * Ritual (and similar) auto-select Mixed 3 Pack when it's the only
   * remaining flavour. That programmatic check must not also apply a
   * buy-once default and complete the plan step.
   */
  _shouldSkipUnlockDefault() {
    if (this.defaultSelection !== 'no_subscription') return false;
    const step = this.blockEl.closest('[data-bb-step]') || this.blockEl;
    const checked = step.querySelectorAll(
      '.product_variant_option input[type="radio"]:checked'
    );
    return Array.from(checked).some(
      (input) =>
        input.dataset.autoSelected === 'true' &&
        input.dataset.userSelected !== 'true'
    );
  }

  clearPlanSelection() {
    let notified = false;
    this.blockEl
      .querySelectorAll(
        'input[name="subscription-plan"], input[name="selling_plan_option"]'
      )
      .forEach((input) => {
        if (!input.checked) return;
        input.checked = false;
        if (!notified) {
          input.dispatchEvent(new Event('change', { bubbles: true }));
          notified = true;
        }
      });
    this.updateSelectionState();
  }

  applyDefaultSelection() {
    if (this.defaultSelection === 'none' && !this.hideNoSub) return;

    if (this.defaultSelection === 'subscription' || (this.hideNoSub && this.defaultSelection !== 'no_subscription')) {
      let subInput = null;
      if (this.useColorMatch) {
        const activeSubSet = this.blockEl.querySelector('.sub-colour-set.active');
        if (activeSubSet) {
          subInput = activeSubSet.querySelector('input[name="subscription-plan"]');
        }
      } else {
        subInput = this.blockEl.querySelector('input[name="subscription-plan"]:not(.no-subscription-input)');
      }
      if (this.isSellingPlanOnly) {
        subInput = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
      }
      if (subInput && !subInput.disabled) {
        subInput.checked = true;
        subInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (this.defaultSelection === 'no_subscription' && !this.hideNoSub) {
      let noSubInput = null;
      if (this.useColorMatch && !this.isAddon) {
        const activeNoSubSet = this.blockEl.querySelector('.no-sub-colour-set.active');
        if (activeNoSubSet) {
          noSubInput = activeNoSubSet.querySelector('.no-subscription-input');
        }
      } else {
        noSubInput = this.blockEl.querySelector('.no-subscription-input');
      }
      if (this.isSellingPlanOnly) {
        noSubInput = this.blockEl.querySelector('#' + this.blockId + '-buy_once');
      }
      if (noSubInput) {
        noSubInput.checked = true;
        noSubInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    this.updateSelectionState();
    this.syncCompactPriceInPlanRow();
    this.syncSimplePlanSubtitle();
    this.updatePricePerDay();
  }

  // ------------------------------------------------------------------
  //  Reset (after add-to-cart)
  // ------------------------------------------------------------------

  onProductSelectionsCleared() {
    setTimeout(() => {
      const allInputs = this.blockEl.querySelectorAll('input[type="radio"]');
      allInputs.forEach(input => { input.checked = false; });

      const fieldset = this.getFieldset();
      if (fieldset) {
        fieldset.removeAttribute('data-sticky-selection');
        fieldset.removeAttribute('data-selection');
        fieldset.setAttribute('data-sticky-selection', '');
        fieldset.classList.remove('not-loaded');
      }

      this.checkProgressiveDisclosure();

      const variantInput = this._getScopedVariantInput();
      const shouldStayLocked = this.blockEl.dataset.progressiveDisclosure === 'true' &&
        variantInput && variantInput.hasAttribute('data-options-unselected');

      if (!shouldStayLocked && this.defaultSelection !== 'none') {
        setTimeout(() => this.applyDefaultSelection(), 20);
      }
    }, 100);
  }

  // ------------------------------------------------------------------
  //  Plan Dropdown
  // ------------------------------------------------------------------

  onDropdownChange(dropdown, options = {}) {
    const selectedOpt = dropdown.options[dropdown.selectedIndex];
    if (!selectedOpt) return;
    // `autoSync: true` is passed from internal callers that sync the
    // dropdown without an explicit user action (initial bootstrap and
    // variant changes). In that mode we MUST NOT force-check the
    // `buy_plan` radio — doing so silently overrides the merchant's
    // "Default Selection" setting and the user's stored preference.
    var autoSync = !!options.autoSync;

    const planIndex = parseInt(selectedOpt.value, 10);
    const sellingPlanId = selectedOpt.dataset.sellingPlanId || '';
    const variantId = selectedOpt.dataset.variantId || '';
    const quantity = selectedOpt.dataset.quantity || '1';

    this.blockEl.querySelectorAll('[data-plan-thumbnails] > div').forEach(row => {
      const rowIndex = parseInt(row.dataset.planIndex, 10);
      row.style.display = rowIndex === planIndex ? '' : 'none';
    });

    const deliveryInterval = selectedOpt.dataset.deliveryInterval || '';
    const visibleRow = this.blockEl.querySelector('[data-plan-thumbnails] > div[data-plan-index="' + planIndex + '"]:not([data-combo])');
    if (visibleRow) {
      const intervalEl = visibleRow.querySelector('.delivery-interval');
      if (intervalEl) {
        intervalEl.textContent = deliveryInterval;
        intervalEl.style.display = deliveryInterval ? '' : 'none';
      }
    }

    this.blockEl.querySelectorAll('[data-delivery-col-value]').forEach(el => {
      el.textContent = deliveryInterval;
    });

    if (deliveryInterval) {
      this.blockEl.dataset.mainDeliveryInterval = deliveryInterval;
    }

    this.blockEl.querySelectorAll('[data-plan-picker-option]').forEach(opt => {
      opt.classList.toggle('is-selected', opt.dataset.planIndex === String(planIndex));
    });

    this.syncCompactPriceInPlanRow();
    this.syncSimplePlanSubtitle();
    this.updatePricePerDay();

    if (this.isSellingPlanOnly) {
      const buyPlan = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
      if (buyPlan && !autoSync) buyPlan.checked = true;
      this.updateSellingPlanOnATC();
      this.updateSelectionState();
      return;
    }

    const activeSubInput = this._getActiveSubInput();

    if (activeSubInput) {
      const comboData = this._getComboDropdownData();
      let newSellingPlan = sellingPlanId;
      let newVariant = variantId || (activeSubInput.value || '').split(',')[0];
      let newQty = quantity;

      if (comboData) {
        newSellingPlan += ',' + comboData.sellingPlanId;
        newVariant += ',' + comboData.variantId;
        newQty += ',' + comboData.quantity;
        activeSubInput.dataset.comboPrice = comboData.price;
        activeSubInput.dataset.comboCompare = comboData.comparePrice;
        activeSubInput.dataset.comboQty = comboData.quantity;
      } else {
        delete activeSubInput.dataset.comboPrice;
        delete activeSubInput.dataset.comboCompare;
        delete activeSubInput.dataset.comboQty;
      }

      activeSubInput.dataset.sellingPlan = newSellingPlan;
      activeSubInput.value = newVariant;
      activeSubInput.dataset.quantity = newQty;

      // Keep cosmetic pack size in sync with the selected plan option so ATC
      // can stamp `_display_pack_quantity` independently of cart quantity.
      if (selectedOpt.dataset.displayPackQuantity) {
        activeSubInput.dataset.displayPackQuantity = selectedOpt.dataset.displayPackQuantity;
      } else {
        delete activeSubInput.dataset.displayPackQuantity;
      }
      if (selectedOpt.dataset.packQuantity) {
        activeSubInput.dataset.packQuantity = selectedOpt.dataset.packQuantity;
      } else {
        delete activeSubInput.dataset.packQuantity;
      }

      activeSubInput.checked = true;
      activeSubInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updateSelectionState();
    this.updateAddItemBoxPrice();

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.updatePricing) {
      gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
    }
  }

  // ------------------------------------------------------------------
  //  Combo Plan Dropdown Change
  // ------------------------------------------------------------------

  onComboDropdownChange(comboDropdown) {
    const selectedOpt = comboDropdown.options[comboDropdown.selectedIndex];
    if (!selectedOpt) return;

    const comboIdx = parseInt(selectedOpt.value, 10);

    this.blockEl.querySelectorAll('[data-combo-thumbnails] .combo-row').forEach(row => {
      const rowIdx = parseInt(row.dataset.comboPlanIndex, 10);
      row.style.display = rowIdx === comboIdx ? '' : 'none';
    });

    this.blockEl.querySelectorAll('[data-combo-plan-picker-option]').forEach(opt => {
      opt.classList.toggle('is-selected', opt.dataset.comboPlanIndex === String(comboIdx));
    });

    const activeSubInput = this._getActiveSubInput();
    if (!activeSubInput) return;

    const mainSpId = (activeSubInput.dataset.sellingPlan || '').split(',')[0] || '';
    const mainVariant = (activeSubInput.value || '').split(',')[0] || '';
    const mainQty = (activeSubInput.dataset.quantity || '1').split(',')[0] || '1';

    const comboSpId = selectedOpt.dataset.sellingPlanId || '';
    const comboVariantId = selectedOpt.dataset.variantId || '';
    const comboQty = selectedOpt.dataset.quantity || '1';
    const comboPrice = selectedOpt.dataset.price || '0';
    const comboCompare = selectedOpt.dataset.comparePrice || comboPrice;

    activeSubInput.dataset.sellingPlan = mainSpId + ',' + comboSpId;
    activeSubInput.value = mainVariant + ',' + comboVariantId;
    activeSubInput.dataset.quantity = mainQty + ',' + comboQty;
    activeSubInput.dataset.comboPrice = comboPrice;
    activeSubInput.dataset.comboCompare = comboCompare;
    activeSubInput.dataset.comboQty = comboQty;

    const comboDelivery = selectedOpt.dataset.deliveryInterval || '';
    if (comboDelivery) {
      this.blockEl.dataset.comboDeliveryInterval = comboDelivery;
      this.blockEl.querySelectorAll('[data-combo-delivery-col-value]').forEach(el => {
        el.textContent = comboDelivery;
      });
    }

    this.updateAddItemBoxPrice();
    this.syncCompactPriceInPlanRow();
    this.syncSimplePlanSubtitle();
    this.updatePricePerDay();

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.updatePricing) {
      gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
    }
  }

  _getActiveSubInput() {
    if (this.isSellingPlanOnly) {
      return this.blockEl.querySelector('input[name="subscription-plan"]');
    }
    if (this.useColorMatch) {
      const activeSubSet = this.blockEl.querySelector('.sub-colour-set.active');
      return activeSubSet ? activeSubSet.querySelector('input[name="subscription-plan"]') : null;
    }
    return this.blockEl.querySelector('input[name="subscription-plan"]:not(.no-subscription-input)');
  }

  _getComboDropdownData() {
    const comboDropdown = this.blockEl.querySelector('[data-combo-plan-dropdown]');
    if (!comboDropdown) {
      if (this.blockEl.dataset.hasCombo !== 'true') return null;
      const activeSubInput = this._getActiveSubInput();
      if (!activeSubInput || !activeSubInput.dataset.comboPrice) return null;
      return {
        sellingPlanId: (activeSubInput.dataset.sellingPlan || '').split(',')[1] || '',
        variantId: (activeSubInput.value || '').split(',')[1] || '',
        quantity: (activeSubInput.dataset.quantity || '1').split(',')[1] || '1',
        price: activeSubInput.dataset.comboPrice || '0',
        comparePrice: activeSubInput.dataset.comboCompare || '0'
      };
    }
    const opt = comboDropdown.options[comboDropdown.selectedIndex];
    if (!opt) return null;
    return {
      sellingPlanId: opt.dataset.sellingPlanId || '',
      variantId: opt.dataset.variantId || '',
      quantity: opt.dataset.quantity || '1',
      price: opt.dataset.price || '0',
      comparePrice: opt.dataset.comparePrice || '0'
    };
  }

  // ------------------------------------------------------------------
  //  Variant → Plan Filtering (SPO mode)
  // ------------------------------------------------------------------

  _parseVariantPlanLinks(linksStr, universalStr) {
    const universalIndices = universalStr ? universalStr.split(',').map(s => parseInt(s, 10)) : [];
    const map = {};
    linksStr.split('|').forEach(entry => {
      const [vid, idx] = entry.split(':');
      if (!vid || idx === undefined) return;
      if (!map[vid]) map[vid] = [...universalIndices];
      map[vid].push(parseInt(idx, 10));
    });
    // Also store universal indices for variants that have no explicit links
    map._universal = universalIndices;
    return map;
  }

  // True when `vid` is part of this block's offer. Blocks without explicit
  // variant→plan links accept any variant (universal plans).
  _knowsVariant(vid) {
    const links = this.blockEl.dataset.variantPlanLinks || '';
    if (!links) return true;
    const target = String(vid);
    return links.split('|').some(pair => pair.split(':')[0] === target);
  }

  // The variant this block actually subscribes — used when the host bundle
  // step belongs to a different product than the block's sub_product
  // (color-matched `selling_plan_only` blocks).
  _ownVariantId() {
    const checked = this.blockEl.querySelector(
      'input[name="selling_plan_option"]:checked, input[name="subscription-plan"]:checked'
    );
    if (checked && checked.dataset && checked.dataset.variantId && this._knowsVariant(checked.dataset.variantId)) {
      return checked.dataset.variantId;
    }
    return this.blockEl.dataset.initialVariantId || '';
  }

  _getCurrentVariantId() {
    // 0. Step-scoped input — multi-product pages (bundle builder) wrap each
    //    step; the first global match would belong to ANOTHER product.
    const step = this.blockEl.closest('[data-bb-step]');
    if (step) {
      const stepInput = step.querySelector('input[data-default-option]');
      const stepVal = stepInput && stepInput.value ? String(stepInput.value) : '';
      // A color-matched `selling_plan_only` block can subscribe a DIFFERENT
      // product than the step it lives in. In that case the step's variant
      // isn't part of this block's offer, so prefer the block's own variant
      // (otherwise the subscribe option never reveals — no matching plan).
      if (stepVal && this._knowsVariant(stepVal)) return stepVal;
      const ownVid = this._ownVariantId();
      if (ownVid) return ownVid;
      return stepVal;
    }
    // 1. Checked [data-product-selection] radio — universal across all picker types
    const checked = document.querySelector('[data-product-selection]:checked');
    if (checked && checked.value) return checked.value;
    // 2. Standard hidden inputs (variant-options.liquid sets these)
    const h = document.querySelector('input[name="id"]');
    if (h && h.value) return h.value;
    const s = document.getElementById('selected-variant-id');
    if (s && s.value) return s.value;
    // 3. Visible price inside buy-once label as last resort
    const buyOnceLabel = this.blockEl.querySelector('label[for="' + this.blockId + '-buy_once"]');
    if (buyOnceLabel) {
      const vp = buyOnceLabel.querySelector('.price[data-variant-id]:not([style*="display: none"])');
      if (vp && vp.dataset.variantId) return vp.dataset.variantId;
    }
    return '';
  }

  _findBuyPlanLabel() {
    const radio = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
    if (!radio) return null;
    // Label may be a parent (wrapped) or a sibling (for= attribute)
    return radio.closest('label') || this.blockEl.querySelector('label[for="' + radio.id + '"]');
  }

  // ------------------------------------------------------------------
  //  Plan-picker chrome visibility
  //  Hides the dropdown affordance (chevron + `plan-picker__trigger`
  //  cursor styling + slide-down panel) when the active variant has
  //  only one allowed plan. Run on init AND on every variant change
  //  so SSR mismatches — e.g. multiple plans rendered for a variant
  //  that's actually linked to a single plan — self-correct without
  //  requiring the shopper to click a variant first.
  // ------------------------------------------------------------------
  _applyPlanPickerVisibility(allowedIndices) {
    if (!Array.isArray(allowedIndices)) return;

    const planThumbnails = this.blockEl.querySelector('[data-plan-thumbnails]');
    const planPickerPanel = this.blockEl.querySelector('[data-plan-picker-panel]');

    if (planPickerPanel) {
      const opts = planPickerPanel.querySelectorAll('[data-plan-picker-option]');
      let firstOpt = null;
      opts.forEach(opt => {
        const planIdx = parseInt(opt.dataset.planIndex, 10);
        const visible = allowedIndices.includes(planIdx);
        opt.style.display = visible ? '' : 'none';
        if (visible && !firstOpt) firstOpt = opt;
        opt.classList.remove('is-selected');
      });
      if (firstOpt) firstOpt.classList.add('is-selected');
    }

    const visiblePlanCount = allowedIndices.length;
    const planPicker = this.blockEl.querySelector('[data-plan-picker]');
    if (planPicker) planPicker.style.display = '';
    if (planPickerPanel) {
      if (visiblePlanCount <= 1) {
        planPickerPanel.style.display = 'none';
        planPickerPanel.classList.remove('is-open');
      } else {
        planPickerPanel.style.display = '';
      }
    }
    const chevron = (planPicker || planThumbnails)?.querySelector('.plan-picker__chevron');
    if (chevron) chevron.style.display = visiblePlanCount > 1 ? '' : 'none';
    if (planThumbnails) {
      planThumbnails.classList.toggle('plan-picker__trigger', visiblePlanCount > 1);
    }
  }

  onVariantChanged(variantId) {
    if (!this._variantPlanMap || !this.isSellingPlanOnly) return;

    const vid = String(variantId);
    if (vid === this._lastVariantId) return;
    this._lastVariantId = vid;

    const allowedIndices = this._variantPlanMap[vid] || this._variantPlanMap._universal || [];
    const hasPlansForVariant = allowedIndices.length > 0;
    const hadPlansBefore = this._lastHadPlans;
    this._lastHadPlans = hasPlansForVariant;

    // Always update price display for the active variant
    this.updateVisibleVariantPrices(vid);

    const dropdown = this.blockEl.querySelector('[data-plan-dropdown]');

    if (!hasPlansForVariant) {
      // Only toggle visibility when transitioning FROM plans → no plans
      if (hadPlansBefore !== false) {
        const buyPlanRadio = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
        const buyPlanLabel = this._findBuyPlanLabel();
        const buyOnceRadio = this.blockEl.querySelector('#' + this.blockId + '-buy_once');
        if (buyPlanRadio) buyPlanRadio.style.display = 'none';
        if (buyPlanLabel) buyPlanLabel.style.display = 'none';
        this.blockEl.querySelectorAll('[data-plan-thumbnails], [data-plan-picker], [data-delivery-columns], [data-combo-delivery-columns], [data-combo-thumbnails], [data-combo-plan-picker]').forEach(el => el.style.display = 'none');
        if (buyOnceRadio) buyOnceRadio.checked = true;
      }
      this.updateSellingPlanOnATC();
      this.updateAtcText();
      this.updateSelectionState();
      return;
    }

    // Only toggle visibility when transitioning FROM no plans → has plans
    if (hadPlansBefore === false) {
      const buyPlanRadio = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
      const buyPlanLabel = this._findBuyPlanLabel();
      const buyOnceRadio = this.blockEl.querySelector('#' + this.blockId + '-buy_once');
      const buyOnceLabel = buyOnceRadio
        ? (buyOnceRadio.closest('label') || this.blockEl.querySelector('label[for="' + buyOnceRadio.id + '"]'))
        : null;
      // Restore the subscription radio's visibility, but ONLY check it
      // if the user previously expressed a subscribe preference or the
      // merchant's default is subscription. Defaulting to checked here
      // would override `defaultSelection = 'no_subscription' | 'none'`.
      if (buyPlanRadio) {
        buyPlanRadio.style.display = '';
        const userPref = window.subscriptionPreference;
        const shouldCheckPlan =
          userPref === 'subscription' ||
          (!userPref && (this.defaultSelection === 'subscription' ||
            (this.hideNoSub && this.defaultSelection !== 'no_subscription')));
        if (shouldCheckPlan) buyPlanRadio.checked = true;
      }
      if (buyPlanLabel) buyPlanLabel.style.display = '';
      if (buyOnceRadio) buyOnceRadio.style.display = '';
      if (buyOnceLabel) buyOnceLabel.style.display = '';
      this.blockEl.querySelectorAll('[data-plan-thumbnails], [data-plan-picker], [data-delivery-columns], [data-combo-delivery-columns], [data-combo-thumbnails], [data-combo-plan-picker]').forEach(el => el.style.display = '');
    }

    // Update plan content (dropdown, thumbnails, picker) — runs for every variant change
    if (dropdown) {
      let firstVisibleIdx = -1;
      Array.from(dropdown.options).forEach(opt => {
        const planIdx = parseInt(opt.value, 10);
        const visible = allowedIndices.includes(planIdx);
        opt.hidden = !visible;
        opt.disabled = !visible;
        if (visible && firstVisibleIdx === -1) firstVisibleIdx = planIdx;
      });
      if (firstVisibleIdx >= 0) {
        const firstOpt = Array.from(dropdown.options).find(o => parseInt(o.value, 10) === firstVisibleIdx && !o.hidden);
        if (firstOpt) {
          dropdown.value = firstOpt.value;
          this.onDropdownChange(dropdown, { autoSync: true });
        }
      }
    }

    const planThumbnails = this.blockEl.querySelector('[data-plan-thumbnails]');
    if (planThumbnails) {
      const rows = planThumbnails.querySelectorAll('[data-plan-index]');
      let firstShown = false;
      rows.forEach(row => {
        const planIdx = parseInt(row.dataset.planIndex, 10);
        const visible = allowedIndices.includes(planIdx);
        row.style.display = visible && !firstShown ? '' : 'none';
        if (visible && !firstShown) firstShown = true;
      });
    }

    this._applyPlanPickerVisibility(allowedIndices);

    this.updateSellingPlanOnATC();
    this.updateAtcText();
    this.updateSelectionState();
    this.syncCompactPriceInPlanRow();
    this.syncSimplePlanSubtitle();
    this.updatePricePerDay();
  }

  // ------------------------------------------------------------------
  //  Custom Plan Picker — thumbnails area triggers slide-down panel
  // ------------------------------------------------------------------

  bindPlanPicker() {
    this.blockEl.querySelectorAll('[data-plan-picker]:not([data-combo-plan-picker])').forEach(picker => {
      const panel = picker.querySelector('[data-plan-picker-panel]');
      const trigger = picker.querySelector('[data-plan-picker-trigger]');
      if (!panel || !trigger) return;

      trigger.addEventListener('click', (e) => {
        if (this.blockEl.dataset.locked === 'true') return;

        e.preventDefault();
        e.stopPropagation();

        const parentLabel = trigger.closest('label');
        if (parentLabel) {
          const forId = parentLabel.getAttribute('for');
          const radio = forId ? document.getElementById(forId) : parentLabel.querySelector('input[type="radio"]');
          if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        const isOpen = picker.classList.contains('is-open');
        this._closeAllPlanPickers();
        if (!isOpen) this._openPlanPanel(picker, panel);
      });

      panel.querySelectorAll('[data-plan-picker-option]').forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const planIndex = option.dataset.planIndex;

          panel.querySelectorAll('[data-plan-picker-option]').forEach(o => o.classList.remove('is-selected'));
          option.classList.add('is-selected');

          const nativeSelect = this.blockEl.querySelector('[data-plan-dropdown]');
          if (nativeSelect) {
            nativeSelect.value = planIndex;
            this.onDropdownChange(nativeSelect);
          }

          this._closePlanPanel(picker, panel);
        });
      });
    });

    this.bindComboPlanPicker();
    document.addEventListener('click', () => this._closeAllPlanPickers());
  }

  bindComboPlanPicker() {
    this.blockEl.querySelectorAll('[data-combo-plan-picker]').forEach(picker => {
      const panel = picker.querySelector('[data-combo-plan-picker-panel]');
      const trigger = picker.querySelector('[data-combo-plan-picker-trigger]');
      if (!panel || !trigger) return;

      trigger.addEventListener('click', (e) => {
        if (this.blockEl.dataset.locked === 'true') return;
        e.preventDefault();
        e.stopPropagation();

        const isOpen = picker.classList.contains('is-open');
        this._closeAllPlanPickers();
        if (!isOpen) this._openPlanPanel(picker, panel);
      });

      panel.querySelectorAll('[data-combo-plan-picker-option]').forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const comboIdx = option.dataset.comboPlanIndex;

          panel.querySelectorAll('[data-combo-plan-picker-option]').forEach(o => o.classList.remove('is-selected'));
          option.classList.add('is-selected');

          const comboSelect = this.blockEl.querySelector('[data-combo-plan-dropdown]');
          if (comboSelect) {
            comboSelect.value = comboIdx;
            this.onComboDropdownChange(comboSelect);
          }

          this._closePlanPanel(picker, panel);
        });
      });
    });

    const comboDD = this.blockEl.querySelector('[data-combo-plan-dropdown]');
    if (comboDD) {
      comboDD.addEventListener('change', () => this.onComboDropdownChange(comboDD));
    }
  }

  _openPlanPanel(picker, panel) {
    clearTimeout(picker._closeTimer);
    picker.classList.add('is-open');
    panel.style.maxHeight = panel.scrollHeight + 'px';
  }

  _closePlanPanel(picker, panel) {
    panel.style.maxHeight = '0';
    clearTimeout(picker._closeTimer);
    picker._closeTimer = setTimeout(() => picker.classList.remove('is-open'), 250);
  }

  _closeAllPlanPickers() {
    this.blockEl.querySelectorAll('[data-plan-picker]:not([data-combo-plan-picker])').forEach(picker => {
      const panel = picker.querySelector('[data-plan-picker-panel]');
      if (panel) this._closePlanPanel(picker, panel);
    });
    this.blockEl.querySelectorAll('[data-combo-plan-picker]').forEach(picker => {
      const panel = picker.querySelector('[data-combo-plan-picker-panel]');
      if (panel) this._closePlanPanel(picker, panel);
    });
  }

  // ------------------------------------------------------------------
  //  OTP Conversion Strip (expand when buy-once selected)
  // ------------------------------------------------------------------

  bindOtpConversionStrip() {
    const strips = this.blockEl.querySelectorAll('[data-otp-conversion-strip]');
    if (!strips.length) return;

    const radioName = this.isSellingPlanOnly ? 'selling_plan_option' : 'subscription-plan';

    this.blockEl.querySelectorAll('input[name="' + radioName + '"]').forEach(input => {
      input.addEventListener('change', () => this._syncOtpStrips(), true);
    });

    strips.forEach(strip => {
      const btn = strip.querySelector('[data-otp-switch-to-sub]');
      if (!btn) return;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._switchToSubscription();
      });
    });
  }

  _syncOtpStrips() {
    const isBuyOnce = !this.isSubscriptionChecked();
    this.blockEl.querySelectorAll('[data-otp-conversion-strip]').forEach(strip => {
      const label = strip.closest('label');
      if (!label) return;
      const input = label.previousElementSibling;
      const isThisChecked = input && input.checked;

      if (isBuyOnce && isThisChecked) {
        this._openOtpStrip(strip);
      } else {
        this._closeOtpStrip(strip);
      }
    });
  }

  _openOtpStrip(strip) {
    if (strip.classList.contains('is-expanded')) return;

    strip.style.transition = 'none';
    strip.classList.add('is-expanded');
    void strip.offsetHeight;
    const targetHeight = strip.scrollHeight;

    strip.style.opacity = '0';
    strip.style.padding = '0';
    strip.style.marginTop = '0';
    strip.style.maxHeight = '0';
    void strip.offsetHeight;

    strip.style.transition = '';
    void strip.offsetHeight;

    strip.style.maxHeight = targetHeight + 'px';
    strip.style.removeProperty('opacity');
    strip.style.removeProperty('padding');
    strip.style.removeProperty('margin-top');
  }

  _closeOtpStrip(strip) {
    if (!strip.classList.contains('is-expanded')) return;

    strip.style.maxHeight = strip.scrollHeight + 'px';
    strip.style.opacity = '1';
    strip.style.padding = '9px 12px 9px 10px';
    strip.style.marginTop = '4px';
    void strip.offsetHeight;

    strip.style.maxHeight = '0';
    strip.style.opacity = '0';
    strip.style.padding = '0';
    strip.style.marginTop = '0';

    strip.addEventListener('transitionend', function handler(e) {
      if (e.propertyName !== 'max-height') return;
      strip.removeEventListener('transitionend', handler);
      strip.classList.remove('is-expanded');
      strip.style.removeProperty('max-height');
      strip.style.removeProperty('opacity');
      strip.style.removeProperty('padding');
      strip.style.removeProperty('margin-top');
    });
  }

  _switchToSubscription() {
    const radioName = this.isSellingPlanOnly ? 'selling_plan_option' : 'subscription-plan';
    let subInput = null;

    if (this.useColorMatch) {
      const activeSubSet = this.blockEl.querySelector('.sub-colour-set.active');
      if (activeSubSet) subInput = activeSubSet.querySelector('input[name="' + radioName + '"]');
    }
    if (!subInput && this.isSellingPlanOnly) {
      subInput = this.blockEl.querySelector('#' + this.blockId + '-buy_plan');
    }
    if (!subInput) {
      subInput = this.blockEl.querySelector('input[name="' + radioName + '"]:not(.no-subscription-input):not([id$="-buy_once"])');
    }

    if (subInput) {
      subInput.checked = true;
      subInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ------------------------------------------------------------------
  //  Event Binding
  // ------------------------------------------------------------------

  bindEvents() {
    const radioName = this.isSellingPlanOnly ? 'selling_plan_option' : 'subscription-plan';

    // Buy-once quantity stepper (+/-) — classic PDPs only; the bundle
    // builder binds its own copies via BundleBuilder.js.
    this._bindBuyOnceQuantities();

    // Radio change — track preference and update state
    this.blockEl.querySelectorAll('input[name="' + radioName + '"]').forEach(input => {
      const handler = (event) => {
        if (event && event.isTrusted) {
          const isSub = !input.classList.contains('no-subscription-input') &&
                        input.id !== (this.blockId + '-buy_once');
          window.subscriptionPreference = isSub ? 'subscription' : 'no-subscription';
        }

        if (this.isSellingPlanOnly) {
          this.updateSellingPlanOnATC();
        }

        this.updateMainProductSkip();
        this.updateSelectionState();
        this.syncOneTimePurchasePrice();
        this.syncCompactPriceInPlanRow();
    this.syncSimplePlanSubtitle();
        this.updatePricePerDay();
        this.toggleDoubleUpVisibility();

        const gp = window.PXUTheme?.jsGlobalProduct;
        if (gp?.updatePricing) {
          gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
        }
      };
      input.addEventListener('change', handler, true);
    });

    // Upsell-only mode — checkbox is the sole step selection
    const upsellInput = this.blockEl.querySelector('[data-upsell-atc-input]');
    if (upsellInput && !upsellInput.classList.contains('subscription-upsell-option-input')) {
      const upsellHandler = () => {
        this.updateSelectionState();
        const gp = window.PXUTheme?.jsGlobalProduct;
        if (gp?.updatePricing) {
          gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
        }
      };
      upsellInput.addEventListener('change', upsellHandler);
      this._cleanup.push(() => upsellInput.removeEventListener('change', upsellHandler));
    }

    // When additional offer exists, make primary subscription radios deselectable (behave like checkboxes)
    if (this.hasAdditionalOffer) {
      this.blockEl.querySelectorAll('input[name="' + radioName + '"]').forEach(input => {
        if (input.classList.contains('no-subscription-input')) return;
        let lastCheckedState = input.checked;
        const clickToggleHandler = (e) => {
          if (lastCheckedState && input.checked) {
            input.checked = false;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
          lastCheckedState = input.checked;
        };
        input.addEventListener('click', clickToggleHandler);
        this._cleanup.push(() => input.removeEventListener('click', clickToggleHandler));

        const changeTracker = () => { lastCheckedState = input.checked; };
        input.addEventListener('change', changeTracker);
        this._cleanup.push(() => input.removeEventListener('change', changeTracker));
      });
    }

    // Additional subscription checkbox (dual-offer mode)
    if (this.hasAdditionalOffer) {
      const addlInput = this.blockEl.querySelector('[data-additional-sub-input]');
      if (addlInput) {
        const addlHandler = (event) => {
          if (addlInput.checked) {
            const noSubInput = this.blockEl.querySelector('.no-subscription-input');
            if (noSubInput && noSubInput.checked) {
              noSubInput.checked = false;
            }
          }
          this.updateMainProductSkip();
          this.updateSelectionState();
          const gp = window.PXUTheme?.jsGlobalProduct;
          if (gp?.updatePricing) {
            gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
          }
        };
        addlInput.addEventListener('change', addlHandler);
        this._cleanup.push(() => addlInput.removeEventListener('change', addlHandler));
      }

      // Additional offer dropdown
      this.blockEl.querySelectorAll('[data-addl-plan-dropdown]').forEach(dd => {
        const ddHandler = () => {
          const selectedOpt = dd.options[dd.selectedIndex];
          if (!selectedOpt || !addlInput) return;
          const spId = selectedOpt.dataset.sellingPlanId || '';
          const qty = selectedOpt.dataset.quantity || '1';
          const variantId = selectedOpt.dataset.variantId || '';
          addlInput.dataset.sellingPlan = spId;
          addlInput.dataset.quantity = qty;
          if (variantId) {
            addlInput.value = variantId;
            addlInput.dataset.variantId = variantId;
          }
          addlInput.checked = true;
          addlInput.dispatchEvent(new Event('change', { bubbles: true }));
        };
        dd.addEventListener('change', ddHandler);
        this._cleanup.push(() => dd.removeEventListener('change', ddHandler));
      });

      // Override no-subscription click to also deselect additional offer
      const noSubInputs = this.blockEl.querySelectorAll('.no-subscription-input');
      noSubInputs.forEach(noSubInput => {
        const noSubAddlHandler = () => {
          if (noSubInput.checked && addlInput) {
            addlInput.checked = false;
          }
        };
        noSubInput.addEventListener('change', noSubAddlHandler);
        this._cleanup.push(() => noSubInput.removeEventListener('change', noSubAddlHandler));
      });
    }

    // Plan dropdown change (native select kept as data source)
    this.blockEl.querySelectorAll('[data-plan-dropdown]').forEach(dd => {
      dd.addEventListener('change', () => this.onDropdownChange(dd));
    });

    // Custom plan picker interactions
    this.bindPlanPicker();

    // OTP conversion message strip (expand/collapse + switch button)
    this.bindOtpConversionStrip();

    // Color sync via variantColorSelected
    const colorHandler = (event) => {
      // Step scoping: inside a bundle-builder step, only react to colour
      // changes that originate from the SAME step — another product's
      // colour (e.g. toothbrush black vs bottle black) must not re-match
      // this block.
      const ownStep = this.blockEl.closest('[data-bb-step]');
      if (ownStep) {
        const srcInput = event.detail && event.detail.input;
        if (!srcInput || srcInput.closest('[data-bb-step]') !== ownStep) return;
      }
      const newColorHandle = event.detail && event.detail.variant;
      const selectedVariantInput = this._getScopedVariantInput();
      const activeVariantId = selectedVariantInput && selectedVariantInput.value;
      if (newColorHandle || activeVariantId) {
        this.onColorChanged(newColorHandle || '', activeVariantId);
        if (newColorHandle) this.syncAdditionalOfferColor(newColorHandle);
        this.syncUpsellColor(newColorHandle || '', activeVariantId);
      }
      setTimeout(() => this.syncOneTimePurchasePrice(), 50);
    };
    document.addEventListener('variantColorSelected', colorHandler, true);
    this._cleanup.push(() => document.removeEventListener('variantColorSelected', colorHandler, true));

    // Reset after add to cart
    const clearHandler = () => this.onProductSelectionsCleared();
    document.addEventListener('productSelectionsCleared', clearHandler);
    this._cleanup.push(() => document.removeEventListener('productSelectionsCleared', clearHandler));

    // Variant change — sync prices, color, and plan filtering
    const changeHandler = (e) => {
      // Kit upsell lives outside the subscription block; always sync OTP
      // breakdown when kit/upsell ATC selection changes (even across steps).
      if (
        e.target.matches &&
        (e.target.matches('[data-kit-selection-input]') ||
          e.target.matches('[data-upsell-atc-input]'))
      ) {
        setTimeout(() => {
          this.syncOtpBreakdown();
          this.syncSubscribeUpsellDiscountBadge();
        }, 20);
      }

      // Step scoping: inside a bundle-builder step, ignore selection
      // changes from other steps/products entirely.
      const ownStep = this.blockEl.closest('[data-bb-step]');
      if (ownStep && e.target.closest &&
          e.target.closest('[data-bb-step]') !== ownStep) {
        return;
      }

      // A changed variant that this block doesn't offer (e.g. a sibling
      // product in another bundle step) must NOT be fed to onVariantChanged
      // verbatim — it isn't in this block's plan map and would wrongly hide
      // the subscribe option. Resolve to the block's own variant instead.
      const resolveVid = (raw) => (this._knowsVariant(raw) ? raw : this._getCurrentVariantId());

      if (e.target.matches && e.target.matches('[data-product-selection]')) {
        if (this._variantPlanMap && e.target.value) {
          const vid = resolveVid(e.target.value);
          if (vid) this.onVariantChanged(vid);
        }
        setTimeout(() => this.syncOneTimePurchasePrice(), 10);
        // Only re-match colour-sets when the MAIN product variant changes.
        // Plan radios are also [data-product-selection]; syncing from them
        // re-entered onColorChanged and rewrote the ATC label.
        const fromOwnPlanRadio =
          this.blockEl.contains(e.target) &&
          e.target.matches(
            'input[name="subscription-plan"], input[name="selling_plan_option"]'
          );
        if (!fromOwnPlanRadio) {
          setTimeout(() => this.syncSelectionToCurrentVariantColor(), 0);
        }
      }
      if (e.target.matches && e.target.matches('input[name="id"]')) {
        if (this._variantPlanMap) {
          const vid = resolveVid(e.target.value);
          if (vid) this.onVariantChanged(vid);
        }
        setTimeout(() => this.syncOneTimePurchasePrice(), 10);
        setTimeout(() => this.syncSelectionToCurrentVariantColor(), 0);
      }
      if (this._variantPlanMap && this._isOwnVariantInput(e.target)) {
        const vid = resolveVid(e.target.value);
        if (vid) this.onVariantChanged(vid);
        setTimeout(() => this.syncSelectionToCurrentVariantColor(), 0);
      }
    };
    document.addEventListener('change', changeHandler);
    this._cleanup.push(() => document.removeEventListener('change', changeHandler));

    // Variant→plan filtering: multiple strategies to detect variant changes
    if (this._variantPlanMap) {
      let lastVid = '';

      const getVariantId = () => this._getCurrentVariantId();

      const fireVariantChange = (source) => {
        const newVid = getVariantId();
        if (newVid && newVid !== lastVid) {
          lastVid = newVid;
          this.onVariantChanged(lastVid);
        }
      };

      // Strategy 1: document-level click (variant buttons are outside this block)
      const clickHandler = () => setTimeout(() => fireVariantChange('click'), 80);
      document.addEventListener('click', clickHandler);
      this._cleanup.push(() => document.removeEventListener('click', clickHandler));

      // Strategy 2: poll for value changes every 200ms
      const pollInterval = setInterval(() => fireVariantChange('poll'), 200);
      this._cleanup.push(() => clearInterval(pollInterval));

      // Strategy 3: observe price-div visibility within our block as a
      //             secondary signal — if the theme shows a different
      //             price-<vid> div, derive the active variant from it
      const buyOnceLabel = this.blockEl.querySelector('label[for="' + this.blockId + '-buy_once"]');
      if (buyOnceLabel) {
        const priceObserver = new MutationObserver(() => {
          const visiblePrice = buyOnceLabel.querySelector('.price[data-variant-id]:not([style*="display: none"])');
          if (visiblePrice) {
            const vid = visiblePrice.dataset.variantId;
            if (vid && vid !== lastVid) {
              lastVid = vid;
              this.onVariantChanged(lastVid);
            }
          }
        });
        priceObserver.observe(buyOnceLabel, { subtree: true, attributes: true, attributeFilter: ['style'], childList: true });
        this._cleanup.push(() => priceObserver.disconnect());
      }
    }

    // Progressive disclosure — watch variant input
    if (this.blockEl.dataset.progressiveDisclosure === 'true') {
      const variantInput = this._getScopedVariantInput();
      if (variantInput) {
        const observer = new MutationObserver(() => this.refreshLockState());
        observer.observe(variantInput, { attributes: true, attributeFilter: ['data-options-unselected'] });
        this._cleanup.push(() => observer.disconnect());
      }

      // Inside a bundle builder the lock also depends on EARLIER steps (other
      // products' options and plans). Those live outside this block's scoped
      // variant input, so re-evaluate whenever anything in the bundle changes.
      const bundle = this.blockEl.closest('[data-bundle-builder]');
      if (bundle) {
        const bundleChangeHandler = (e) => {
          if (
            e.target &&
            e.target.matches &&
            e.target.matches('input[type="radio"], input[type="checkbox"]')
          ) {
            this.refreshLockState();
          }
        };
        bundle.addEventListener('change', bundleChangeHandler);
        this._cleanup.push(() =>
          bundle.removeEventListener('change', bundleChangeHandler)
        );
      }
    }
  }

  // ------------------------------------------------------------------
  //  Initialization
  // ------------------------------------------------------------------

  init() {
    if (!window.subscriptionPreference) {
      window.subscriptionPreference = null;
    }

    this.bindEvents();
    this.checkProgressiveDisclosure();

    // Remove loading state
    const fieldset = this.getFieldset();
    if (fieldset) fieldset.classList.remove('not-loaded');

    // Don't apply a plan default while earlier progressive steps are still
    // incomplete — otherwise buy-once / subscribe would land in totals
    // before the shopper has finished the previous product.
    const isLocked =
      this.blockEl.dataset.progressiveDisclosure === 'true' &&
      this.blockEl.dataset.locked === 'true';
    if (isLocked) {
      this.clearPlanSelection();
    } else {
      this.applyDefaultSelection();
    }

    // Variant→plan filtering: apply initial filter based on current variant
    if (this._variantPlanMap) {
      const initVid = this._getCurrentVariantId();
      if (initVid && String(initVid) !== this._lastVariantId) {
        // Variant differs from Liquid SSR — re-run full filtering
        this.onVariantChanged(initVid);
      } else if (initVid) {
        // Liquid already rendered the correct state for this variant;
        // just sync the dropdown selection and selling plan on ATC.
        const allowedIndices = this._variantPlanMap[String(initVid)]
          || this._variantPlanMap._universal || [];
        // SSR can over-render the picker chrome — it uses `main_plan_count`
        // (the total number of plans) for `plan-picker__trigger` / chevron
        // visibility, which is wrong when only a subset of plans applies to
        // the active variant. Re-evaluate against `allowedIndices` now so
        // single-plan variants don't appear as a clickable dropdown.
        this._applyPlanPickerVisibility(allowedIndices);

        const dropdown = this.blockEl.querySelector('[data-plan-dropdown]');
        if (dropdown) {
          const firstOpt = Array.from(dropdown.options).find(
            o => allowedIndices.includes(parseInt(o.value, 10)) && !o.hidden
          );
          if (firstOpt && dropdown.value !== firstOpt.value) {
            dropdown.value = firstOpt.value;
          }
          this.onDropdownChange(dropdown, { autoSync: true });
        }
      }
    }

    if (this.isSellingPlanOnly) {
      this.updateSellingPlanOnATC();
    }

    this.updateMainProductSkip();
    this.updateSelectionState();
    this.syncOneTimePurchasePrice();
    this.syncCompactPriceInPlanRow();
    this.syncSimplePlanSubtitle();
    this.updatePricePerDay();
    this.syncSelectionToCurrentVariantColor();

    const gp = window.PXUTheme?.jsGlobalProduct;
    if (gp?.updatePricing) {
      gp.updatePricing(document.querySelectorAll('[data-product-selection]'));
    }

    setTimeout(() => this.updateAtcText(), 50);

    // Sync OTP conversion strips with initial selection state
    setTimeout(() => this._syncOtpStrips(), 100);

    // Double Up feature (bundle colour match)
    this.initDoubleUp();
  }

  // ------------------------------------------------------------------
  //  Cleanup (for reloadProductForm)
  // ------------------------------------------------------------------

  destroy() {
    this._cleanup.forEach(fn => fn());
    this._cleanup = [];
  }
}

window.PXUTheme.jsGlobalProduct = {
  buttonLabels: {},
  _initialFormHTML: null,
  _subscriptionControllers: [],

  // ------------------------------------------------------------------
  //  Subscription Block Orchestrator
  //  Picks up configs registered by inline Liquid scripts and creates
  //  a SubscriptionBlockController for each block on the page.
  // ------------------------------------------------------------------
  initSubscriptionBlocks() {
    this._subscriptionControllers.forEach(c => c.destroy());
    this._subscriptionControllers = [];

    const queue = window._subscriptionBlockQueue || [];
    queue.forEach(entry => {
      if (!entry.blockEl) return;
      const controller = new SubscriptionBlockController(entry.blockEl, entry);
      controller.init();
      this._subscriptionControllers.push(controller);
    });
    window._subscriptionBlockQueue = [];
  },

  async init() {
    // Capture pristine HTML before any handlers modify the DOM
    if (!this._initialFormHTML) {
      this.captureInitialFormState();
    }
    
    this.initButtonLabels();
    this.watchSwappers(); // Watch for changes in variant
    if (document.querySelector("[data-product-selection]")) {
      this.watchInputs();
    }
    if (document.querySelector("[data-product-atc]")) {
      this.watchATCs();
    }
    if (document.querySelector(".global-product__atc-bar")) {
      this.watchATCScroll();
    }
    if (document.querySelectorAll("[data-sticky-selection]") && document.getElementById("getStartedBtn")) {
      this.updateFloatingATC();
    }
    this.updateKlarnaPrice();

    if (document.querySelectorAll("input[data-update-selected-variant]")) {
      this.watchSelectedVariantChanges();
    }

    if (document.body.getAttribute("data-intelligems-enabled") === "true") {
      this.initIntelligems();
    }

    this.outOfStockHandler(false);

    this.onLoadElements();

    // Ensure Back in Stock widget is positioned inside the last product options block
    this.positionBackInStockWidget();

    // Initialize progressive disclosure for all option blocks
    this.initProgressiveDisclosure();

    // Initialize subscription block controllers from inline Liquid configs
    this.initSubscriptionBlocks();

    // Watch for text changes on main ATC button and sync to sticky button
    this.watchMainATCTextChanges();
    
    // Watch for out of stock text changes and disable buttons
    this.watchOutOfStockTextChanges();
  },

  initButtonLabels() {
    const atcBtn = document.querySelector("[data-product-atc]");
    const getStartedBtn = document.getElementById("getStartedBtn");
    const getStartedTextEl = getStartedBtn ? getStartedBtn.querySelector(".text") : null;
    const getStartedInitialText = getStartedTextEl ? getStartedTextEl.textContent.trim() : "";

    this.buttonLabels = {
      addToCart: (atcBtn?.dataset.labelAddToCart || (atcBtn?.textContent || "")).trim(),
      outOfStock: atcBtn?.dataset.oosLabel || "",
      selectOptions: atcBtn?.dataset.labelSelectOptions || "",
      preorder: atcBtn?.dataset.labelPreorder || "",
      floatingAddToCart: getStartedBtn?.dataset.labelAddToCart || atcBtn?.dataset.labelAddToCart || (atcBtn?.textContent || "").trim(),
      floatingOutOfStock: getStartedBtn?.dataset.labelOutOfStock || atcBtn?.dataset.oosLabel || "",
      floatingSelectOptions: getStartedBtn?.dataset.labelSelectOptions || atcBtn?.dataset.labelSelectOptions || "",
      floatingDefault: getStartedBtn?.dataset.labelDefault || getStartedInitialText
    };
  },

  getButtonLabel(key, fallback = "") {
    if (!this.buttonLabels) {
      return fallback;
    }
    return this.buttonLabels[key] || fallback;
  },

  shouldPreserveButtonText(button, event) {
    if (!button || !button.classList.contains("restock-rocket-preorder-button")) {
      return false;
    }

    if (event && event.target && event.target.matches("[data-product-selection]")) {
      const target = event.target;
      const isSubscriptionOption =
        target.name === "subscription-plan" ||
        target.name === "selling_plan_option" ||
        target.closest(".global-product__global-subscription") ||
        target.closest(".global-product__options__subscription") ||
        target.hasAttribute("data-subscription-option");

      if (!isSubscriptionOption) {
        return false;
      }
    }

    return true;
  },

  getActiveSubscriptionLabel() {
    const block = document.querySelector('.subscription-options-block[data-subscribe-atc-text]');
    if (!block) return null;
    const label = block.dataset.subscribeAtcText;
    if (!label) return null;
    const isSPO = block.dataset.sellingPlanOnly === 'true';
    const radioName = isSPO ? 'selling_plan_option' : 'subscription-plan';
    const checked = block.querySelector('input[name="' + radioName + '"]:checked');
    if (!checked) return null;
    if (checked.classList.contains('no-subscription-input')) return null;
    if (checked.classList.contains('subscription-upsell-option-input')) return null;
    if (isSPO && checked.id && checked.id.endsWith('-buy_once')) return null;
    return label;
  },

  updateKlarnaPrice() {
    const klarnaEl = document.querySelector("klarna-placement");
    const priceEl = document.querySelector("[data-klarna-price]");

    if (klarnaEl && priceEl) {
      const price = Number(priceEl.dataset.klarnaPrice);

      if (price < 5000) {
        klarnaEl.classList.add("hidden");
      } else {
        klarnaEl.classList.remove("hidden");
      }

      klarnaEl.setAttribute("data-purchase-amount", price);

      window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
      window.KlarnaOnsiteService.push({ eventName: "refresh-placements" });

      klarnaEl.classList.add("Reveal");
    }
  },

  watchSelectedVariantChanges() {
    const selectedVariantInputs = document.querySelectorAll("input[data-update-selected-variant]");

    if (selectedVariantInputs.length === 0) {
      console.warn("No selected variant inputs found.");
      return;
    }

    selectedVariantInputs.forEach((input) => {
      input.addEventListener("change", (event) => {
        const newVariant = event.target.value;
        document.querySelector("input[name='id']").value = newVariant;
      });
    });
  },

  watchDisabledBlocks() {
    const products = document.querySelectorAll("product");
    let enableDisabled = true;

    for (const prod of products) {
      if (prod.dataset.skipForSubscription === "true") continue;
      if (!prod.querySelector("[data-product-selection]:checked")) {
        enableDisabled = false;
      }
    }

    if (enableDisabled) {
      const disabledBlocks = document.querySelectorAll("[data-product-disabled]");
      for (const block of disabledBlocks) {
        block.classList.add("enabled");
      }
    }
  },

  disableDisabledBlocks() {
    const disabledBlocks = document.querySelectorAll("[data-product-disabled]");
    for (const block of disabledBlocks) {
      block.classList.remove("enabled");
    }
  },

  // Watch inputs to show subscription options
  watchInputs() {
    if (this._subscriptionActivated === undefined) {
      this._subscriptionActivated = false;
    }
    
    // Remove previous document-level change handler to prevent stacking
    if (this._watchInputsChangeHandler) {
      document.removeEventListener("change", this._watchInputsChangeHandler);
    }
    
    const updateHandler = () => {
      const inputs = document.querySelectorAll("[data-product-selection]");
      const subscriptionBlock = document.querySelector(".global-product__options__subscription");
      
      if (subscriptionBlock) {
        subscriptionBlock.classList.add("active");
      }
      this.watchDisabledBlocks();
      this.updatePricing(inputs);
    };

    const togglePaymentButtons = () => {
      const paymentButtons = document.querySelectorAll('.shopify-payment-button, .additional_payment_container');
      
      // Check all subscription input types (unified block uses subscription-plan / selling_plan_option)
      const checkedInputs = document.querySelectorAll(
        'input[name="subscription-plan"]:checked, input[name="selling_plan_option"]:checked'
      );
      
      let hasSubscription = false;
      checkedInputs.forEach(input => {
        if (input.dataset.sellingPlan) {
          hasSubscription = true;
        }
      });
      
      paymentButtons.forEach(button => {
        if (hasSubscription) {
          button.style.display = 'none';
        } else {
          button.style.display = '';
        }
      });
    };

    this._watchInputsChangeHandler = (event) => {
      if (event.target.matches("[data-product-selection]")) {
        updateHandler();
      }
      
      // Toggle payment buttons when subscription options change
      if (event.target.matches('input[name="subscription-plan"], input[name="selling_plan_option"]')) {
        togglePaymentButtons();
      }
      
      const colorOption = event.target.closest('[data-color-option]');
      if (colorOption && event.target.matches('input[type="radio"]')) {
        const input = event.target;
        const colorValue = input.dataset.colour || input.value;
        const label = input.closest('label');
        const title = label?.dataset?.title || '';
        document.dispatchEvent(new CustomEvent('variantColorSelected', {
          detail: {
            variant: colorValue,
            color: colorValue,
            title: title,
            source: 'colorOption',
            input: input
          }
        }));
      }
      
      this.outOfStockHandler(event);
      setTimeout(() => this.outOfStockHandler(false), 0);
    };

    document.addEventListener("change", this._watchInputsChangeHandler);
    
    togglePaymentButtons();
  },

  outOfStockHandler(event) {
    const btn = document.querySelector("[data-product-atc]");

    if (event) {
      if (btn) {
        const oosLabel = this.getButtonLabel("outOfStock", btn.dataset.oosLabel || "");
        const addToCartLabel = this.getButtonLabel("addToCart", btn.dataset.labelAddToCart || (btn.textContent || "").trim());
        if (event.target.matches("[data-out-of-stock='true']")) {
          btn.classList.add("disabled");
          btn.setAttribute("aria-disabled", "true");
          if (!this.shouldPreserveButtonText(btn, event)) {
            btn.textContent = oosLabel || addToCartLabel || btn.textContent;
          }
        } else {
          btn.classList.remove("disabled");
          btn.setAttribute("aria-disabled", "false");
          if (!this.shouldPreserveButtonText(btn, event)) {
            const subLabel = this.getActiveSubscriptionLabel();
            btn.textContent = subLabel || addToCartLabel || btn.textContent;
          }
        }
      }

      const additionalPaymentContainer = document.querySelector(".global-product .additional_payment_container");
      if (additionalPaymentContainer && additionalPaymentContainer.dataset.paymentButtonsEnabled !== "false") {
        if (event.target.matches("[data-out-of-stock='true']")) {
          additionalPaymentContainer.classList.add("hidden");
        } else {
          additionalPaymentContainer.classList.remove("hidden");
        }
      }

      return;
    } else {
      const inputs = document.querySelectorAll("[data-product-selection]");
      if (!inputs.length) return;

      let allOutOfStock = true;
      let checkedOutOfStock = false;
      let hasOptionsUnselected = false;

      for (const input of inputs) {
        const isOutOfStock = input.dataset.outOfStock === "true";
        const isOptionsUnselected = input.hasAttribute("data-options-unselected");

        // Only consider out-of-stock status if options are selected
        if (!isOptionsUnselected) {
          if (!isOutOfStock) {
            allOutOfStock = false;
          }

          if (input.checked && isOutOfStock) {
            checkedOutOfStock = true;
          }
        }

        if (input.checked && isOptionsUnselected) {
          hasOptionsUnselected = true;
        }
      }

      // Subscription gating: every `.subscription-options-block` on the page
      // must have at least one of its radios checked for the main ATC to be
      // enabled. When the block's `default_selection` is `none` (and no
      // manual click has happened yet), NONE of its radios are preselected
      // at SSR — without this check the ATC would remain enabled even
      // though the merchant required the customer to pick a plan.
      //
      // Covers both modes the unified block emits:
      //   - `add_item`         → radios named `subscription-plan`
      //   - `selling_plan_only`→ radios named `selling_plan_option`
      if (!hasOptionsUnselected) {
        const subBlocks = document.querySelectorAll(
          ".subscription-options-block"
        );
        for (const subBlock of subBlocks) {
          const radios = subBlock.querySelectorAll(
            'input[name="subscription-plan"], input[name="selling_plan_option"]'
          );
          if (radios.length === 0) continue;
          const anyChecked = Array.from(radios).some((r) => r.checked);
          if (!anyChecked) {
            hasOptionsUnselected = true;
            break;
          }
        }
      }

      // Disable button if: options unselected OR variant is out of stock (when options are selected)
      const disableButton = hasOptionsUnselected || checkedOutOfStock || (!hasOptionsUnselected && allOutOfStock);

      const additionalPaymentContainer = document.querySelector(".global-product .additional_payment_container");

      if (disableButton) {
        if (btn) {
          btn.classList.add("disabled");
          btn.setAttribute("aria-disabled", "true");
          const addToCartLabel = this.getButtonLabel("addToCart", btn.dataset.labelAddToCart || (btn.textContent || "").trim());
          const oosLabel = this.getButtonLabel("outOfStock", btn.dataset.oosLabel || "");
          const subLabel = this.getActiveSubscriptionLabel();
          const preserveText = this.shouldPreserveButtonText(btn);
          if (hasOptionsUnselected) {
            if (!preserveText) {
              btn.textContent = subLabel || addToCartLabel || btn.textContent;
            }
            btn.setAttribute("data-options-unselected", "");
          } else if (checkedOutOfStock) {
            if (!preserveText) {
              btn.textContent = oosLabel || addToCartLabel || btn.textContent;
            }
            btn.removeAttribute("data-options-unselected");
          } else {
            if (!preserveText) {
              btn.textContent = subLabel || addToCartLabel || btn.textContent;
            }
            btn.removeAttribute("data-options-unselected");
          }
        }
        if (additionalPaymentContainer && !hasOptionsUnselected) {
          additionalPaymentContainer.classList.add('hidden');
        }
        // Auto-show BIS under options if present (with slide-in).
        // Only show if out of stock AND all options are selected.
        // Debounced — Core/model swaps fire many intermediate OOS updates.
        const hiddenId = document.querySelector("input[name='id']");
        this.setBackInStockWidgetVisible(
          !hasOptionsUnselected && checkedOutOfStock,
          hiddenId && hiddenId.value
        );
      } else {
        if (btn) {
          btn.classList.remove("disabled");
          btn.setAttribute("aria-disabled", "false");
          if (!this.shouldPreserveButtonText(btn)) {
            const subLabel = this.getActiveSubscriptionLabel();
            const addToCartLabel = this.getButtonLabel("addToCart", btn.dataset.labelAddToCart || (btn.textContent || "").trim());
            btn.textContent = subLabel || addToCartLabel || btn.textContent;
          }
          btn.removeAttribute("data-options-unselected");
        }
        if (additionalPaymentContainer && additionalPaymentContainer.dataset.paymentButtonsEnabled !== "false") {
          additionalPaymentContainer.classList.remove('hidden');
        }

        this.setBackInStockWidgetVisible(false);
      }
      
      // Keep sticky ATC in sync
      if (this.updateFloatingATC) this.updateFloatingATC();
    }
  },

  getKitComboHeaderPriceConfig() {
    const parts = Array.from(document.querySelectorAll('[data-kit-combo-header-price-part]'));
    const configEl = parts.find((el) => el.hasAttribute('data-kit-combo-preferred')) || parts[0];
    if (!configEl) return null;

    try {
      const config = JSON.parse(configEl.textContent);
      if (!config || typeof config.cents !== 'number') return null;
      return {
        cents: config.cents,
        formatted: config.formatted,
        showFrom: configEl.getAttribute('data-show-from') === 'true'
      };
    } catch (err) {
      return null;
    }
  },

  /**
   * Returns true when a real subscription (a selling plan) is currently
   * selected. When `blockId` is provided, the check is scoped to the
   * Global Subscription block carrying that `data-subscription-block-id`,
   * so a Kit Component upsell discount can target one specific block.
   */
  isSubscriptionSelected(blockId) {
    let scopes;
    const targetBlock = blockId
      ? document.querySelector(`[data-subscription-block-id="${blockId}"]`)
      : null;
    if (targetBlock) {
      // A matching block id was found — scope strictly to it.
      scopes = [targetBlock];
    } else {
      // No block id given, or the configured block id isn't present on this
      // template/view (e.g. the merchant didn't set `block_identifier` here).
      // Fall back to checking any subscription block so the gated discount
      // still responds to a subscription selection instead of silently failing.
      scopes = Array.from(document.querySelectorAll('.subscription-options-block'));
      if (scopes.length === 0) scopes = [document];
    }

    for (const scope of scopes) {
      const checked = scope.querySelectorAll(
        'input[name="subscription-plan"]:checked, input[name="selling_plan_option"]:checked'
      );
      for (const input of checked) {
        // Skip explicit "no subscription" / "buy once" options.
        if (input.classList.contains('no-subscription-input')) continue;
        if (input.id && input.id.endsWith('-buy_once')) continue;
        // A real subscription is either a radio carrying a selling plan
        // (add-item layout) or the "buy plan" radio (selling-plan-only
        // layout, where the plan id is resolved from the plan picker).
        if (input.dataset.sellingPlan) return true;
        if (input.id && input.id.endsWith('-buy_plan')) return true;
      }
    }
    return false;
  },

  /**
   * Keeps each kit-selection-upsell ATC input's `data-price` in sync with
   * its configured display-only discount. The discount is applied when
   * configured, and — for subscription-gated discounts — only while the
   * relevant subscription is selected. `data-price` is used for display
   * math only, so this never changes the amount charged at checkout.
   */
  syncUpsellDiscountPrices() {
    const upsellInputs = document.querySelectorAll(
      'input[data-upsell-atc-input][data-upsell-discount-type]'
    );
    upsellInputs.forEach((input) => {
      const type = input.getAttribute('data-upsell-discount-type');
      const value = parseFloat(input.getAttribute('data-upsell-discount-value')) || 0;
      const fullPrice =
        Number(input.getAttribute('data-upsell-full-price')) ||
        Number(input.dataset.price) ||
        0;
      const requiresSub =
        input.getAttribute('data-upsell-discount-requires-subscription') === 'true';
      const blockId =
        input.getAttribute('data-upsell-discount-subscription-block-id') || '';

      let applyDiscount = value > 0;
      if (applyDiscount && requiresSub) {
        applyDiscount = this.isSubscriptionSelected(blockId);
      }

      let displayPrice = fullPrice;
      if (applyDiscount) {
        if (type === 'percentage') {
          displayPrice = Math.round(fullPrice - (fullPrice * value) / 100);
        } else if (type === 'fixed') {
          displayPrice = Math.round(fullPrice - value * 100);
        }
        if (displayPrice < 0) displayPrice = 0;
      }
      input.dataset.price = String(displayPrice);
    });
  },

  updatePricing(inputs) {
    this.syncUpsellDiscountPrices();
    let price = 0;
    let comparePrice = 0;
    let sellingPlan = false;
    let sellingPlanMainOnly = 0;
    let sellingPlanComboOnly = 0;
    let sellingPlanLines = [];
    // Track variant-only price (excludes subscription inputs) for price/reviews display
    let variantOnlyPrice = 0;
    let variantOnlyComparePrice = 0;
    // Track selected main variant pricing for savings-breakdown visibility rules
    let selectedVariantPrice = 0;
    let selectedVariantComparePrice = 0;
    // Track one-time kit-selection-upsell line items separately. These are
    // independent products added alongside the main subscription product
    // (e.g. a bottle accompanying refills) and must NOT receive the
    // subscription discount. We subtract them from variantOnlyPrice before
    // applying the multiplier and add them back at full price below.
    let upsellLineTotal = 0;
    let upsellLineCompareTotal = 0;
    let upsellLineSelected = false;

    for (const input of inputs) {
      // Skip inputs inside a product element marked for subscription replacement
      const parentProduct = input.closest("product");
      if (parentProduct && parentProduct.dataset.skipForSubscription === "true") continue;

      if (input.checked == true) {
        const isSubscriptionInput = input.dataset.sellingPlan || 
          input.closest('.global-product__global-subscription') ||
          input.closest('.global-product__options__subscription') || 
          input.name === 'subscription-plan' ||
          input.name === 'selling_plan_option';
        const isMainVariantInput = !isSubscriptionInput;
        // One-time kit-selection-upsell items (e.g. bottle alongside refills).
        // We must exclude these from the subscription-discount calculation so
        // the displayed total reflects discount(refills × qty) + upsell once.
        const isUpsellLine = input.hasAttribute && input.hasAttribute('data-upsell-atc-input');

        const qtyStr = input.dataset.quantity || '0';
        const inputQty = Number(qtyStr.split(',')[0]) || 0;
        const inputPrice = Number(input.dataset.price || 0);
        const inputCompare = Number(input.dataset.comparePrice || 0);

        let comboAddon = 0;
        let comboCompareAddon = 0;
        const cPrice = Number(input.dataset.comboPrice || 0);
        const cCompare = Number(input.dataset.comboCompare || cPrice);
        const cQty = Number(input.dataset.comboQty || 0);
        if (cPrice > 0 && cQty > 0) {
          comboAddon = cPrice * cQty;
          comboCompareAddon = cCompare * cQty;
        }

        if (inputQty > 0) {
          price = Number(price) + inputPrice * inputQty + comboAddon;
          if (inputCompare > 0) {
            comparePrice = Number(comparePrice) + inputCompare * inputQty + comboCompareAddon;
          } else {
            comparePrice = Number(comparePrice) + inputPrice * inputQty + comboCompareAddon;
          }
          if (isMainVariantInput) {
            selectedVariantPrice = Number(selectedVariantPrice) + inputPrice * inputQty + comboAddon;
            if (inputCompare > 0) {
              selectedVariantComparePrice = Number(selectedVariantComparePrice) + inputCompare * inputQty + comboCompareAddon;
            }
          }
          if (!isSubscriptionInput) {
            variantOnlyPrice = Number(variantOnlyPrice) + inputPrice * inputQty + comboAddon;
            if (inputCompare > 0) {
              variantOnlyComparePrice = Number(variantOnlyComparePrice) + inputCompare * inputQty + comboCompareAddon;
            } else {
              variantOnlyComparePrice = Number(variantOnlyComparePrice) + inputPrice * inputQty + comboCompareAddon;
            }
          }
          if (isUpsellLine) {
            upsellLineSelected = true;
            upsellLineTotal += inputPrice * inputQty + comboAddon;
            upsellLineCompareTotal += (inputCompare > 0 ? inputCompare : inputPrice) * inputQty + comboCompareAddon;
          }
        } else {
          price = Number(price) + inputPrice + comboAddon;
          if (inputCompare > 0) {
            comparePrice = Number(comparePrice) + inputCompare + comboCompareAddon;
          } else {
            comparePrice = Number(comparePrice) + inputPrice + comboCompareAddon;
          }
          if (isMainVariantInput) {
            selectedVariantPrice = Number(selectedVariantPrice) + inputPrice + comboAddon;
            if (inputCompare > 0) {
              selectedVariantComparePrice = Number(selectedVariantComparePrice) + inputCompare + comboCompareAddon;
            }
          }
          if (!isSubscriptionInput) {
            variantOnlyPrice = Number(variantOnlyPrice) + inputPrice + comboAddon;
            if (inputCompare > 0) {
              variantOnlyComparePrice = Number(variantOnlyComparePrice) + inputCompare + comboCompareAddon;
            } else {
              variantOnlyComparePrice = Number(variantOnlyComparePrice) + inputPrice + comboCompareAddon;
            }
          }
          if (isUpsellLine) {
            upsellLineSelected = true;
            upsellLineTotal += inputPrice + comboAddon;
            upsellLineCompareTotal += (inputCompare > 0 ? inputCompare : inputPrice) + comboCompareAddon;
          }
        }
        if (input.dataset.sellingPlan) {
          const spLinePrice = inputPrice * inputQty;
          const spLineCombo = comboAddon;
          sellingPlanMainOnly = spLinePrice;
          sellingPlanComboOnly = spLineCombo;
          sellingPlan = (sellingPlan || 0) + spLinePrice + spLineCombo;

          const isAddl = input.dataset.additionalSubInput !== undefined;
          const isDoubleUp = input.dataset.doubleUpInput !== undefined;
          let spLabel = input.dataset.deliveryInterval || '';
          if (!spLabel) {
            const parentBlock = input.closest('.subscription-options-block');
            if (parentBlock) spLabel = parentBlock.dataset.mainDeliveryInterval || parentBlock.dataset.recurrenceLabel || '';
          }
          sellingPlanLines.push({ price: spLinePrice + spLineCombo, label: spLabel, isAdditional: isAddl, isDoubleUp: isDoubleUp });
        }
      }
    }

    // selling_plan_only mode: subscription radios don't carry data-product-selection,
    // so manually integrate the subscription discount into pricing
    const subBlock = document.querySelector('.subscription-options-block');
    if (subBlock && subBlock.dataset.sellingPlanOnly === 'true') {
      const buyPlanChecked = subBlock.querySelector('input[name="selling_plan_option"]:checked');
      const isBuyOnce = buyPlanChecked && buyPlanChecked.id.includes('buy_once');
      const hideNoSub = subBlock.dataset.hideNoSub === 'true';

      if (buyPlanChecked && !isBuyOnce) {
        // Fixed Price Selling Plan: the subscription line is a flat amount that can't be
        // derived from a discount multiplier (the sub price can exceed the buy-once price,
        // e.g. a £19.99 3-pack vs a £10.99 single). The exact figure is emitted on the
        // selected headline as data-sub-price / data-sub-compare, so use it directly.
        const _vidInput = document.querySelector('#selected-variant-id');
        const _vid = _vidInput ? _vidInput.value : '';
        const _subHeadline = _vid
          ? subBlock.querySelector('.price--headline[data-variant-id="' + _vid + '"][data-sub-price]')
          : subBlock.querySelector('.price--headline[data-sub-price]');
        const _subUnit = _subHeadline ? Number(_subHeadline.dataset.subPrice || 0) : 0;
        if (_subUnit > 0) {
          const subCompare = Number(_subHeadline.dataset.subCompare || 0);
          const discountedPrice = _subUnit + upsellLineTotal;
          const discountedCompare = (subCompare > _subUnit ? subCompare : 0) + upsellLineCompareTotal;
          price = discountedPrice;
          comparePrice = discountedCompare > discountedPrice ? discountedCompare : 0;
          sellingPlan = _subUnit;
          if (hideNoSub) {
            variantOnlyPrice = discountedPrice;
            variantOnlyComparePrice = comparePrice || discountedPrice;
          }
        } else {
        const discountEl = subBlock.querySelector('[data-discount]');
        if (discountEl) {
          const multiplier = parseFloat(discountEl.dataset.discount);
          if (multiplier > 0 && multiplier < 1) {
            const rawVariantPrice = variantOnlyPrice;
            const rawComparePrice = variantOnlyComparePrice;
            // Apply the multiplier only to the subscribable portion. Any
            // one-time kit-selection-upsell line items (e.g. a bottle added
            // alongside refills) should be added back at full price.
            const subscribablePortion = Math.max(0, rawVariantPrice - upsellLineTotal);
            const subscribableCompare = Math.max(0, rawComparePrice - upsellLineCompareTotal);
            const discountedSubscribable = Math.round(subscribablePortion * multiplier);
            const discountedPrice = discountedSubscribable + upsellLineTotal;
            const discountedCompare = (subscribablePortion > discountedSubscribable
              ? subscribablePortion
              : subscribableCompare) + upsellLineCompareTotal;

            price = discountedPrice;
            comparePrice = discountedCompare > discountedPrice ? discountedCompare : 0;
            sellingPlan = discountedSubscribable;

            if (hideNoSub) {
              variantOnlyPrice = discountedPrice;
              variantOnlyComparePrice = comparePrice || discountedPrice;
            }
          }
        }
        }
      }
    }

    // When main product is skipped (subscription replaces it), use subscription price
    // for the price/reviews display so it doesn't show zero
    if (variantOnlyPrice === 0 && price > 0) {
      variantOnlyPrice = price;
      variantOnlyComparePrice = comparePrice;
    }

    const hasVariantCompareSavings = selectedVariantComparePrice > 0 && selectedVariantComparePrice > selectedVariantPrice;

    if (document.querySelector("[data-product-price-money]")) {
      const priceMoneyEl = document.querySelector("[data-product-price-money]");
      const moneyFormat = document.body.getAttribute("data-money-format");
      const finalPrice = Shopify.formatMoney(price, moneyFormat);
      const hasCompareSavings = comparePrice > 0 && comparePrice > price;
      const finalComparePrice = hasCompareSavings ? " <span>" + Shopify.formatMoney(comparePrice, moneyFormat) + "</span>" : "";
      // Optional lime-green "Save X%" badge next to the total (Enhanced Total block).
      let savingsBadge = "";
      const enhancedTotalWrap = priceMoneyEl.closest("[data-enhanced-total]");
      if (enhancedTotalWrap && enhancedTotalWrap.dataset.showSavingsBadge === "true" && hasCompareSavings) {
        const savingsPct = Math.round(((comparePrice - price) / comparePrice) * 100);
        if (savingsPct > 0) {
          savingsBadge = ` <span class="slabel slabel-green enhanced-total__savings-badge">Save ${savingsPct}%</span>`;
        }
      }
      const sectionRecurrence = priceMoneyEl.dataset.recurrenceLabel || '';
      // When the cart includes a one-time kit-selection-upsell line item
      // (e.g. bottle alongside refills), the total includes the bottle but
      // only the refills × quantity × discount portion recurs. Annotate the
      // recurrence label so it reads "then £X every Y weeks" against the
      // recurring sub-total, instead of mis-labelling the full kit total
      // as recurring.
      let recurrenceSuffix = '';
      if (sectionRecurrence) {
        const recurringSubtotal = (typeof sellingPlan === 'number' && sellingPlan > 0)
          ? sellingPlan
          : price;
        if (upsellLineTotal > 0 && recurringSubtotal > 0 && recurringSubtotal !== price) {
          const recurringText = Shopify.formatMoney(recurringSubtotal, moneyFormat);
          recurrenceSuffix = ` <span class="total-recurrence total-recurrence--split">then ${recurringText} ${sectionRecurrence}</span>`;
        } else {
          recurrenceSuffix = ` <span class="total-recurrence">${sectionRecurrence}</span>`;
        }
      }
      
      // Check if all options are selected by looking at the variant input
      const variantInput = document.querySelector("#selected-variant-id");
      const allOptionsSelected = variantInput && !variantInput.hasAttribute("data-options-unselected");
      
      // Check if we need to replace "Pending" state
      const isPending = priceMoneyEl.hasAttribute("data-pending-until-selected");
      
      if (isPending) {
        if (allOptionsSelected && price > 0) {
          // All options selected - replace pending state with actual price
          priceMoneyEl.innerHTML = `<span>${finalPrice}</span>${finalComparePrice}${savingsBadge}${recurrenceSuffix}`;
          priceMoneyEl.removeAttribute("data-pending-until-selected");
          
          // Show savings breakdown if hidden
          const savingsBreakdown = document.querySelector("[data-show-when-selected]");
          if (savingsBreakdown) {
            savingsBreakdown.style.display = hasVariantCompareSavings ? "" : "none";
            savingsBreakdown.removeAttribute("data-show-when-selected");
          }
          
          // Show compare price if hidden
          const comparePriceEl = priceMoneyEl.querySelector(".compare-price-value");
          if (comparePriceEl) {
            comparePriceEl.style.display = "";
          }
        }
        // If not all options selected, keep showing "Pending"
      } else {
        // Normal price update (not in pending state)
        priceMoneyEl.innerHTML = `<span>${finalPrice}</span>${finalComparePrice}${savingsBadge}${recurrenceSuffix}`;
      }
    }

    const enhancedTotal = document.querySelector("[data-enhanced-total]");
    if (enhancedTotal) {
      const moneyFormat = document.body.getAttribute("data-money-format");
      const variantInput = document.querySelector("#selected-variant-id");
      const allOptionsSelected = variantInput && !variantInput.hasAttribute("data-options-unselected");
      const primaryPriceEl = enhancedTotal.querySelector("[data-enhanced-total-primary-price]");
      const secondaryPriceEl = enhancedTotal.querySelector("[data-enhanced-total-secondary-price]");
      const recurringNoteEl = enhancedTotal.querySelector("[data-enhanced-total-recurring-note]");
      const shippingNoteEl = enhancedTotal.querySelector("[data-enhanced-total-shipping-note]");
      const pendingPriceEl = enhancedTotal.querySelector(".pending-price");
      const pendingLabel = pendingPriceEl ? pendingPriceEl.textContent : "Pending";
      const notSelectedLabel = enhancedTotal.dataset.notSelectedLabel || "Not selected";
      const addLinkLabel = enhancedTotal.dataset.addLinkLabel || "Add it?";
      const kitSelectorAnchor = enhancedTotal.dataset.kitSelectorAnchor || "#";

      if (!allOptionsSelected || price <= 0) {
        if (primaryPriceEl) {
          primaryPriceEl.classList.remove("enhanced-total__value--action");
          primaryPriceEl.textContent = pendingLabel;
        }
        if (secondaryPriceEl) secondaryPriceEl.textContent = pendingLabel;
        if (recurringNoteEl) {
          recurringNoteEl.hidden = true;
          recurringNoteEl.textContent = "";
        }
        if (shippingNoteEl) {
          shippingNoteEl.hidden = true;
        }
      } else {
        const primaryLinePrice = upsellLineTotal > 0 ? upsellLineTotal : 0;
        const secondaryLinePrice = Math.max(0, price - primaryLinePrice);
        // Per-component compare-at prices (strikethrough RRP for each line).
        const primaryCompare = upsellLineCompareTotal;
        const secondaryCompare = Math.max(0, comparePrice - upsellLineCompareTotal);
        const withCompare = (current, compare) => {
          let html = `<span class="enhanced-total__price-current">${Shopify.formatMoney(current, moneyFormat)}</span>`;
          if (compare > current) {
            html += ` <span class="enhanced-total__price-compare">${Shopify.formatMoney(compare, moneyFormat)}</span>`;
          }
          return html;
        };
        const bottleKitOption = document.querySelector(
          'input[data-kit-selection-input][data-kit-type="product_plus_upsell"]'
        );
        const bottleKitSelected = bottleKitOption
          ? !!document.querySelector(
              'input[data-kit-selection-input][data-kit-type="product_plus_upsell"]:checked'
            )
          : upsellLineSelected;
        if (primaryPriceEl) {
          primaryPriceEl.classList.toggle("enhanced-total__value--action", !bottleKitSelected);
          if (bottleKitSelected) {
            if (primaryLinePrice <= 0) {
              const freeCompare = primaryCompare > 0
                ? ` <span class="enhanced-total__price-compare">${Shopify.formatMoney(primaryCompare, moneyFormat)}</span>`
                : "";
              primaryPriceEl.innerHTML = `<span class="enhanced-total__price-current">Free</span>${freeCompare}`;
            } else {
              primaryPriceEl.innerHTML = withCompare(primaryLinePrice, primaryCompare);
            }
          } else {
            primaryPriceEl.textContent = "";
            const notSelectedText = document.createElement("span");
            notSelectedText.className = "enhanced-total__not-selected";
            notSelectedText.textContent = notSelectedLabel;
            const addLink = document.createElement("a");
            addLink.className = "enhanced-total__add-link";
            addLink.href = kitSelectorAnchor;
            addLink.textContent = addLinkLabel;
            addLink.addEventListener("click", (event) => {
              const targetId = kitSelectorAnchor.replace("#", "");
              const target = document.getElementById(targetId);
              if (!target) return;

              event.preventDefault();

              const stickyHeader = document.querySelector(
                "#mobile-header-sticky-wrapper.is-sticky, .header-sticky-wrapper.is-sticky, .new-header"
              );
              const headerOffset = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
              const targetTop = target.getBoundingClientRect().top + window.pageYOffset;

              window.scrollTo({
                top: Math.max(0, targetTop - headerOffset - 16),
                behavior: "smooth"
              });

              if (window.history && window.history.pushState) {
                window.history.pushState(null, "", kitSelectorAnchor);
              }
            });
            primaryPriceEl.append(notSelectedText, " ", addLink);
          }
        }
        if (secondaryPriceEl) {
          secondaryPriceEl.innerHTML = secondaryLinePrice > 0
            ? withCompare(secondaryLinePrice, secondaryCompare)
            : "-";
        }

        if (recurringNoteEl) {
          const subBlockForLabel = document.querySelector('.subscription-options-block');
          let recurrenceLabel = subBlockForLabel
            ? (subBlockForLabel.dataset.mainDeliveryInterval || subBlockForLabel.dataset.recurrenceLabel || "")
            : "";
          if (!recurrenceLabel && sellingPlanLines.length) {
            recurrenceLabel = sellingPlanLines[0].label || "";
          }
          const hasActiveSubscription = sellingPlan > 0 && recurrenceLabel;
          if (hasActiveSubscription) {
            const template = enhancedTotal.dataset.recurringTemplate || "Then refills from [price] / [interval]";
            recurringNoteEl.textContent = template
              .replace("[price]", Shopify.formatMoney(sellingPlan, moneyFormat))
              .replace("[interval]", recurrenceLabel.toLowerCase());
            recurringNoteEl.hidden = false;
          } else {
            recurringNoteEl.hidden = true;
            recurringNoteEl.textContent = "";
          }
          if (shippingNoteEl) {
            shippingNoteEl.hidden = !hasActiveSubscription;
          }
        } else if (shippingNoteEl) {
          shippingNoteEl.hidden = true;
        }
      }
    }

    // Update savings breakdown if present (for bundle products)
    if (document.querySelector("[data-total-value]") && document.querySelector("[data-savings-value]")) {
      const totalValueEl = document.querySelector("[data-total-value]");
      const savingsValueEl = document.querySelector("[data-savings-value]");
      const savingsBreakdownEl = totalValueEl ? totalValueEl.closest(".savings-breakdown") : null;
      
      if (hasVariantCompareSavings && comparePrice > 0 && comparePrice > price) {
        const savings = comparePrice - price;
        totalValueEl.textContent = Shopify.formatMoney(comparePrice, document.body.getAttribute("data-money-format"));
        savingsValueEl.textContent = "-" + Shopify.formatMoney(savings, document.body.getAttribute("data-money-format"));
        if (savingsBreakdownEl) {
          savingsBreakdownEl.style.display = "";
        }
      } else if (savingsBreakdownEl) {
        savingsBreakdownEl.style.display = "none";
      }
    }

    if (document.querySelector("[data-product-price-text]")) {
      document.querySelector("[data-product-price-text]").dataset.klarnaPrice = price;
    }

    const liquidBreakdown = document.querySelector('[data-recurrence-breakdown]');
    if (liquidBreakdown) liquidBreakdown.style.display = 'none';

    if (sellingPlan && document.querySelector("[data-product-price-text]")) {
      const moneyFmt = document.body.getAttribute("data-money-format");
      const subBlockForLabel = document.querySelector('.subscription-options-block');
      const mainInterval = subBlockForLabel ? (subBlockForLabel.dataset.mainDeliveryInterval || '') : '';
      const comboInterval = subBlockForLabel ? (subBlockForLabel.dataset.comboDeliveryInterval || '') : '';
      const hasCombo = subBlockForLabel && subBlockForLabel.dataset.hasCombo === 'true' && sellingPlanComboOnly > 0;

      if (sellingPlanLines.length > 1) {
        let linesHtml = '';
        sellingPlanLines.forEach((line) => {
          let lineLabel = line.label || mainInterval || (subBlockForLabel ? subBlockForLabel.dataset.recurrenceLabel : '') || 'every 6 months';
          const linePrice = Shopify.formatMoney(line.price, moneyFmt);
          linesHtml += `<span class="total-recurrence-line-js">+ ${linePrice} ${lineLabel.toLowerCase()}</span>`;
        });
        document.querySelector("[data-product-price-text]").innerHTML =
          `Your total today<span class="sub total-recurrence-breakdown-js">${linesHtml}</span>`;
      } else if (hasCombo && mainInterval) {
        const mainPrice = Shopify.formatMoney(sellingPlanMainOnly, moneyFmt);
        const comboPrice = Shopify.formatMoney(sellingPlanComboOnly, moneyFmt);
        const comboLabel = comboInterval || mainInterval;
        document.querySelector("[data-product-price-text]").innerHTML =
          `Your total today<span class="sub total-recurrence-breakdown-js">` +
          `<span class="total-recurrence-line-js">+ ${mainPrice} ${mainInterval.toLowerCase()}</span>` +
          `<span class="total-recurrence-line-js">+ ${comboPrice} ${comboLabel.toLowerCase()}</span>` +
          `</span>`;
      } else {
        const subscriptionPrice = Shopify.formatMoney(sellingPlan, moneyFmt);
        let recurrenceLabel = mainInterval || '';
        if (!recurrenceLabel && subBlockForLabel) {
          recurrenceLabel = subBlockForLabel.dataset.recurrenceLabel || '';
        }
        if (!recurrenceLabel) recurrenceLabel = 'every 6 months';
        document.querySelector("[data-product-price-text]").innerHTML = `Your total today <span class="sub">+ ${subscriptionPrice} ${recurrenceLabel.toLowerCase()}</span>`;
      }
    } else if (document.querySelector("[data-product-price-text]")) {
      const hasSavingsBreakdown = !!document.querySelector(".savings-breakdown");
      document.querySelector("[data-product-price-text]").innerHTML = hasSavingsBreakdown && hasVariantCompareSavings ? `Total Price` : `Your total`;
    }

    // update global-product__price price
    if (document.querySelector(".global-product__price")) {
      const priceEl = document.querySelector(".global-product__price");
      const mirrorTotal = priceEl.hasAttribute('data-mirror-total');
      // Use full price if mirror_total is on, or if variant-only price is 0 (e.g. head options removed the main variant input)
      const useFullPrice = mirrorTotal || (variantOnlyPrice === 0 && price > 0);
      let displayPrice = useFullPrice ? price : variantOnlyPrice;
      let displayComparePrice = useFullPrice ? comparePrice : variantOnlyComparePrice;
      const variantInput = document.querySelector("#selected-variant-id");
      const hasSelection = variantInput && !variantInput.hasAttribute("data-options-unselected");
      const useKitComboFromPrice = priceEl.hasAttribute('data-use-kit-combo-from-price');
      const kitComboConfig = useKitComboFromPrice ? this.getKitComboHeaderPriceConfig() : null;
      const useKitComboInitialPrice = useKitComboFromPrice && kitComboConfig && !hasSelection;

      if (useKitComboInitialPrice) {
        displayPrice = kitComboConfig.cents;
        displayComparePrice = 0;
      } else {
        const fromPriceSource = priceEl.hasAttribute('data-bb-from-price')
          ? priceEl
          : priceEl.querySelector('[data-bb-from-price]');
        const fromPriceCents = fromPriceSource
          ? parseInt(fromPriceSource.getAttribute('data-bb-from-price'), 10)
          : 0;
        const fromCompareCents = fromPriceSource
          ? parseInt(fromPriceSource.getAttribute('data-bb-from-compare'), 10)
          : 0;
        if (!hasSelection && fromPriceCents > 0) {
          displayPrice = fromPriceCents;
          displayComparePrice = fromCompareCents > fromPriceCents ? fromCompareCents : 0;
        }
      }

      const saleLabel = priceEl.querySelector(".slabel");
      const compareAtPriceEl = priceEl.querySelector(".compare-at-price");
      const salePriceWrapper = priceEl.querySelector(".sale_price-wrapper");
      const priceMoney = Shopify.formatMoney(displayPrice, document.body.getAttribute("data-money-format"));
      const compareAtMoney = displayComparePrice > 0 && displayComparePrice > displayPrice ? Shopify.formatMoney(displayComparePrice, document.body.getAttribute("data-money-format")) : null;

      const showKitComboFromPrefix = useKitComboInitialPrice
        && kitComboConfig.showFrom
        && priceEl.hasAttribute('data-display-from');
      let fromText = priceEl.querySelector(".from_text");

      if (showKitComboFromPrefix) {
        const priceContainer = priceEl.querySelector('.price');
        if (!fromText && priceContainer) {
          fromText = document.createElement('span');
          fromText.className = 'from_text';
          fromText.textContent = 'From\u00a0';
          priceContainer.insertBefore(fromText, priceContainer.firstChild);
        }
        if (fromText) fromText.style.display = '';
      } else if (fromText) {
        fromText.style.display = (hasSelection || displayPrice === 0) ? "none" : "";
      }

      if (compareAtPriceEl && compareAtMoney) {
        compareAtPriceEl.textContent = compareAtMoney;
        compareAtPriceEl.style.display = "";
      } else if (compareAtPriceEl) {
        compareAtPriceEl.style.display = "none";
      }

      // Find price element using multiple selectors for compatibility.
      // Prefer a text node over a wrapper that also holds From / compare-at.
      let priceSaleEl = priceEl.querySelector("span[data-ig-pricetext]") ||
                        priceEl.querySelector("span[data-price]") ||
                        priceEl.querySelector("div.price > span.price");
      if (!priceSaleEl) {
        const saleCandidate = priceEl.querySelector(".price--sale");
        if (saleCandidate && !saleCandidate.querySelector(".compare-at-price, .from_text, [data-price]")) {
          priceSaleEl = saleCandidate;
        }
      }
      
      if (priceSaleEl) {
        priceSaleEl.textContent = priceMoney;
        if (displayComparePrice > displayPrice && displayComparePrice > 0) {
          priceSaleEl.classList.add('price--sale');
        } else {
          priceSaleEl.classList.remove('price--sale');
        }
      }

      if (saleLabel && displayComparePrice > displayPrice && displayComparePrice > 0) {
        const percentOff = Math.round(((displayComparePrice - displayPrice) / displayComparePrice) * 100);
        saleLabel.textContent = `${percentOff}% OFF`;
        saleLabel.style.display = "";
      } else if (saleLabel) {
        saleLabel.style.display = "none";
      }

      if (salePriceWrapper) {
        if (displayComparePrice > displayPrice && displayComparePrice > 0) {
          salePriceWrapper.style.display = "";
        } else {
          salePriceWrapper.style.display = "none";
        }
      }

      // Update alternative style price display (price-reviews-container--alt)
      const altPriceContainer = priceEl.querySelector(".price-reviews-container--alt");
      if (altPriceContainer) {
        const comparePriceEl = altPriceContainer.querySelector(".compare-price");
        const currentPriceEl = altPriceContainer.querySelector(".current-price");
        const savingsBadgeEl = altPriceContainer.querySelector(".savings-badge");
        
        if (currentPriceEl) {
          const nowText = currentPriceEl.textContent.includes('Now') ? 'Now ' : '';
          currentPriceEl.textContent = nowText + priceMoney;
        }
        
        if (comparePriceEl && compareAtMoney) {
          const worthText = comparePriceEl.textContent.includes('Worth') ? 'Worth ' : '';
          comparePriceEl.textContent = worthText + compareAtMoney;
          comparePriceEl.style.display = "";
        } else if (comparePriceEl) {
          comparePriceEl.style.display = "none";
        }
        
        if (savingsBadgeEl && displayComparePrice > displayPrice && displayComparePrice > 0) {
          const savings = displayComparePrice - displayPrice;
          const savingsMoney = Shopify.formatMoney(savings, document.body.getAttribute("data-money-format"));
          const savingsClean = savingsMoney.replace(/\.00$/, '');
          const extraSavingsText = savingsBadgeEl.dataset.savingsBadgeExtraText ? ' ' + savingsBadgeEl.dataset.savingsBadgeExtraText : '';
          savingsBadgeEl.textContent = 'Save ' + savingsClean + extraSavingsText;
          savingsBadgeEl.style.display = "";
        } else if (savingsBadgeEl) {
          savingsBadgeEl.style.display = "none";
        }
      }
    }

    this.updateKlarnaPrice();

    if(document.querySelector(".global-product .additional_payment_container") && document.querySelector(".global-product klarna-placement")) {
      const targetContainer = document.querySelector(".global-product .additional_payment_container");
      const klarnaEl = document.querySelector(".global-product klarna-placement");
      if (sellingPlan) {
        targetContainer.classList.add("hidden");
        klarnaEl.classList.add("hidden");
      } else {
        targetContainer.classList.remove("hidden");
        klarnaEl.classList.remove("hidden");
      }
    }

    // Sync each subscription block's per-variant price divs with THAT
    // block's active variant. The variant must be resolved per block — a
    // single global `[data-product-selection]:checked` lookup picks the
    // first step's product on multi-step bundle pages, which matches none
    // of a later block's variants and wrongly hides every price.
    document.querySelectorAll('.subscription-options-block').forEach(function(block) {
      let vid = '';
      const step = block.closest('[data-bb-step]');
      if (step) {
        const links = block.dataset.variantPlanLinks || '';
        const knownIds = links ? links.split('|').map(function(p){ return p.split(':')[0]; }) : null;
        const si = step.querySelector('input[data-default-option]');
        const sv = si && si.value ? String(si.value) : '';
        if (sv && (!knownIds || knownIds.indexOf(sv) !== -1)) {
          vid = sv;
        } else {
          const checkedSub = block.querySelector('input[name="selling_plan_option"]:checked, input[name="subscription-plan"]:checked');
          const csVid = checkedSub && checkedSub.dataset ? String(checkedSub.dataset.variantId || '') : '';
          if (csVid && (!knownIds || knownIds.indexOf(csVid) !== -1)) {
            vid = csVid;
          } else {
            vid = block.dataset.initialVariantId || sv;
          }
        }
      } else {
        const gsel = document.querySelector('[data-product-selection]:checked');
        const hiddenId = document.querySelector('input[name="id"]');
        vid = (gsel && gsel.value) || (hiddenId && hiddenId.value) || ((document.getElementById('selected-variant-id') || {}).value) || '';
      }
      if (!vid) return;
      block.querySelectorAll(
        '.price[data-variant-id], [data-plan-row-price] [data-variant-id], [data-otp-breakdown-c2-price] [data-variant-id]'
      ).forEach(function(el) {
        el.style.display = el.dataset.variantId === String(vid) ? '' : 'none';
      });
    });

    // Keep OTP breakdown + subscribe upsell-discount badge in sync after kit updates.
    if (this._subscriptionControllers && this._subscriptionControllers.length) {
      this._subscriptionControllers.forEach((controller) => {
        if (controller && typeof controller.syncOtpBreakdown === 'function') {
          controller.syncOtpBreakdown();
        }
        if (
          controller &&
          typeof controller.syncSubscribeUpsellDiscountBadge === 'function'
        ) {
          controller.syncSubscribeUpsellDiscountBadge();
        }
      });
    }
  },

  // Watch for changes in variant selection and update gallery
  watchSwappers() {
    const variantSelectors = document.querySelectorAll("[data-swapper]");
    const swappableElements = document.querySelectorAll("[data-colour-swap]");

    if (variantSelectors.length === 0) {
      console.warn("No variant swappers found.");
      return;
    }

    variantSelectors.forEach((swapper) => {
      swapper.addEventListener("click", (event) => {
        const newVariant = event.target.dataset.swapper;
        this.updateGallerySwatches(event.target.dataset.swapper);

        // Scope the colour-set swap to the product / bundle step that owns the
        // clicked swapper. A global query would toggle `.active` on EVERY
        // [data-colour-swap] element on the page, so picking one bundle
        // product's colour would wipe another product's active subscription
        // colour-set (hiding its plan and leaving only "No subscription").
        const swapScope =
          swapper.closest("[data-bb-step]") ||
          swapper.closest("product") ||
          document;
        const scopedSwappable = swapScope.querySelectorAll("[data-colour-swap]");

        if (scopedSwappable.length > 0) {
          for (const el of scopedSwappable) {
            // Subscription colour-sets are owned by SubscriptionBlockController
            // and activate by full variant id (Model + Colour). Toggling them
            // here by colour handle alone resurfaces every matching OTP.
            if (
              el.classList.contains('no-sub-colour-set') ||
              el.classList.contains('sub-colour-set')
            ) {
              continue;
            }
            if (el.dataset.colourSwap === newVariant) {
              el.classList.add("active");
            } else {
              el.classList.remove("active");
            }
          }
          
          // Handle color option inputs (scoped to this swapper's product/step)
          const inputs =
            swapScope === document
              ? document.querySelectorAll("product input")
              : swapScope.querySelectorAll("input");
          for (const input of inputs) {
            if (input.dataset.colour) {
              if (input.dataset.colour === newVariant && input.dataset.defaultOption == "true") {
                input.checked = true;
              }
            }
          }
          
          // Update bundle swatches with new color's variant data
          setTimeout(() => {
            // Update each bundle swatch with the new color's variant data
            const bundleInputs = swapScope.querySelectorAll("input[name='variant'][data-variant-map]");
            bundleInputs.forEach(input => {
              try {
                const variantMap = JSON.parse(input.dataset.variantMap);
                const colorData = variantMap[newVariant];
                
                if (colorData) {
                  // Update input attributes
                  input.value = colorData.id;
                  input.dataset.variantId = colorData.id;
                  input.dataset.igDatatags = colorData.id;
                  input.dataset.price = colorData.price;
                  input.dataset.comparePrice = colorData.compare_price || colorData.price;
                  input.dataset.available = colorData.available;
                  
                  if (colorData.available) {
                    input.removeAttribute('data-out-of-stock');
                  } else {
                    input.dataset.outOfStock = "true";
                  }
                  
                  // If this input is checked, update the hidden variant ID input and refresh payment buttons
                  if (input.checked) {
                    const hiddenVariantInput = document.querySelector("input[name='id']");
                    if (hiddenVariantInput) {
                      hiddenVariantInput.value = colorData.id;
                      // Trigger change event to notify Shopify's dynamic checkout buttons
                      hiddenVariantInput.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                  }
                  
                  // Find the label - it's a sibling, not a parent
                  const label = document.querySelector(`label[for="${input.id}"]`);
                  if (label) {
                    // Update image
                    const img = label.querySelector('[data-bundle-image]');
                    if (img && colorData.image) {
                      // Force image reload by setting src with cache buster
                      img.src = colorData.image + '?t=' + Date.now();
                      // Also update srcset if it exists
                      if (img.srcset) {
                        img.srcset = '';
                      }
                    }
                    
                    // Update price displays
                    const priceEl = label.querySelector('[data-bundle-price]');
                    if (priceEl) {
                      priceEl.textContent = Shopify.formatMoney(colorData.price, document.body.getAttribute("data-money-format"));
                      priceEl.dataset.igPricetext = colorData.id;
                    }
                    
                    const comparePriceEl = label.querySelector('[data-bundle-compare-price]');
                    if (comparePriceEl) {
                      if (colorData.compare_price && colorData.compare_price != colorData.price) {
                        comparePriceEl.textContent = Shopify.formatMoney(colorData.compare_price, document.body.getAttribute("data-money-format"));
                        comparePriceEl.dataset.igComparetext = colorData.id;
                        comparePriceEl.dataset.variantId = colorData.id;
                        comparePriceEl.style.display = '';
                      } else {
                        comparePriceEl.style.display = 'none';
                      }
                    }
                    
                    // Update savings label
                    const savingsEl = label.querySelector('[data-bundle-savings]');
                    if (savingsEl && colorData.compare_price && colorData.compare_price != colorData.price) {
                      const savings = Math.round(((colorData.compare_price - colorData.price) / colorData.compare_price) * 100);
                      savingsEl.textContent = `${savings}% OFF`;
                      savingsEl.dataset.igSavingstext = colorData.id;
                      savingsEl.dataset.variantId = colorData.id;
                      savingsEl.style.display = '';
                    } else if (savingsEl) {
                      savingsEl.style.display = 'none';
                    }
                    
                    // Update out of stock label
                    const oosLabel = label.querySelector('[data-bundle-oos-label]');
                    if (oosLabel) {
                      oosLabel.style.display = colorData.available ? 'none' : '';
                    }
                    
                    // Update label classes
                    if (colorData.available) {
                      label.classList.remove('out-of-stock');
                    } else {
                      label.classList.add('out-of-stock');
                    }
                  }
                }
              } catch (e) {
                console.error('Error updating bundle swatch:', e);
              }
            });
            
            // Update data-sticky-selection attributes for all selected steps
            const stickyElements = document.querySelectorAll("[data-sticky-selection]");
            stickyElements.forEach((stickyEl) => {
              const hasSelection = stickyEl.querySelector("input:checked");
              if (hasSelection) {
                stickyEl.setAttribute("data-selection", "selected");
                stickyEl.setAttribute("data-sticky-selection", "selected");
              }
            });
            
            // Update sticky add to cart button
            if (this.updateFloatingATC) {
              this.updateFloatingATC();
            }
            // Re-check ATC disabled state after swatch updates
            this.outOfStockHandler(false);
            
            // Update tray button text if product instance exists
            if (window.PXUTheme && window.PXUTheme.productSectionInstance && window.PXUTheme.productSectionInstance.updateTrayButton) {
              window.PXUTheme.productSectionInstance.updateTrayButton();
            }
          }, 150);
        } else {
          console.warn("No swappable elements found.");
        }
      });
    });
  },

  updateGallerySwatches(swapper) {
    if (document.querySelector(".gallery-swatches .gallery-swatch")) {
      if (document.querySelector(`.gallery-swatches .gallery-swatch.active`)) document.querySelector(`.gallery-swatches .gallery-swatch.active`).classList.remove("active");
      document.querySelector(`.gallery-swatches .gallery-swatch.swatch-${swapper}`).classList.add("active");
    }
  },

  hasUvCaseSelection(scope) {
    const searchScope = scope || document;
    const variantForm = searchScope.querySelector("#variant-options") || searchScope;
    const selectedInputs = variantForm.querySelectorAll('input[type="radio"]:checked');
    const uvCaseRegex = /uv\s*case/i;

    for (const input of selectedInputs) {
      const candidates = [
        input.dataset.bundleOption,
        input.dataset.optionValue,
        input.dataset.title,
        input.value,
      ];

      if (candidates.some((value) => uvCaseRegex.test(String(value || "")))) {
        return true;
      }
    }

    return false;
  },

  hasActivePaidSubscriptionSelection(scope) {
    const searchScope = scope || document;
    const enabledSubscriptionBlocks = searchScope.querySelectorAll(
      '.subscription-options-block[data-convert-subscription-atc-enabled="true"]'
    );

    if (!enabledSubscriptionBlocks.length) {
      return false;
    }

    for (const block of enabledSubscriptionBlocks) {
      const candidates = block.querySelectorAll(
        'input[name="selling_plan_option"]:checked, input[name="subscription-plan"]:checked, input[name="product-subscription-option"]:checked'
      );

      for (const input of candidates) {
        const id = input.id || "";
        const value = (input.value || "").trim();

        if (id.endsWith("buy_once") || id.endsWith("no-sub") || id.endsWith("no_sub")) {
          continue;
        }

        const label = input.closest("label") || (input.id ? block.querySelector('label[for="' + input.id + '"]') : null);
        if (label && (label.classList.contains("no-subscription-option") || label.closest(".no-subscription-option"))) {
          continue;
        }

        if (input.name === "product-subscription-option" && value === "") {
          continue;
        }

        return true;
      }
    }

    return false;
  },

  triggerConvertEvent(eventKey) {
    if (!window.SuriConvertTracking || typeof window.SuriConvertTracking.trigger !== "function") {
      return;
    }

    window.SuriConvertTracking.trigger(eventKey);
  },

  watchATCs() {
    const addToCartButtons = document.querySelectorAll("[data-product-atc]");

    for (const btn of addToCartButtons) {
      // Remove previous click handlers to avoid duplicates
      btn.replaceWith(btn.cloneNode(true));
    }

    const freshButtons = document.querySelectorAll("[data-product-atc]");
    for (const btn of freshButtons) {
      btn.addEventListener("click", async (event) => {
        // If button is disabled / OOS, toggle Back in Stock widget instead of attempting add to cart
        const isDisabled = btn.classList.contains("disabled") || btn.getAttribute("aria-disabled") === "true";
        if (isDisabled) {
          // Keep original behavior: do nothing when OOS is clicked
          return;
        }

        btn.classList.add("loading");

        let items = [];
        let addProducts = true;
        let sellingPlanParent = btn.dataset.sellingPlan;
        let parent = document;

        if(btn.closest(".global-product")) {
          parent = btn.closest(".global-product");
        }

        // Always get the latest quantity
        const quantity = parseInt(btn.getAttribute("data-quantity"), 10) || 1;

        // Loop through all products and build the cart items
        if (parent.querySelector(".global-product product")) {
          const products = parent.querySelectorAll(".global-product product");
          for (const p of products) {
            // Skip main product when subscription replaces it
            if (p.dataset.skipForSubscription === "true") continue;

            const variant = p.querySelector("[data-product-selection]:checked");

            let allowEmpty = false;
            if (p.dataset.allowEmpty) {
              allowEmpty = true;
            }

            // Skip if no variant is selected
            if (!variant && !allowEmpty) {
              p.scrollIntoView({ behavior: "smooth", block: "center" });
              p.classList.add("highlight");
              setTimeout(() => p.classList.remove("highlight"), 1000);
              addProducts = false;
              btn.classList.remove("loading");
              return;
            }

            if (variant) {
              let ids = variant.value.split(",");
              let sellingPlans = [];
              let sellingPlan = null;

              // Handle selling plans as a comma-separated list, synced with ids
              if (!sellingPlanParent) {
                if (variant.dataset.sellingPlan) {
                  sellingPlans = variant.dataset.sellingPlan.split(",");
                }
              } else {
                if (variant.dataset.sellingPlan) {
                  sellingPlans = variant.dataset.sellingPlan.split(",");
                } else {
                  sellingPlans = sellingPlanParent.split(",");
                }
              }

              // Handle quantities as a comma-separated list, synced with ids
              let quantities = [];
              if (variant.dataset.quantity) {
                quantities = variant.dataset.quantity.split(",");
              } else {
                // fallback to 1 for each id
                quantities = Array(ids.length).fill("1");
              }

              // If sellingPlans length matches ids, sync them up, else fallback to single sellingPlan for all
              let useSyncedSellingPlans = sellingPlans.length === ids.length;
              // If quantities length matches ids, sync them up, else fallback to single quantity for all
              let useSyncedQuantities = quantities.length === ids.length;

              // Check if the URL has the ?FREEWB parameter
              const urlParams = new URLSearchParams(window.location.search);
              const freeWBParam = urlParams.get("FREEWB");

              // If the parameter is set, store a cookie
              if (freeWBParam) {
                document.cookie = "FREEWB=true; path=/; max-age=" + 60 * 60 * 24 * 7; // Cookie valid for 7 days
              }

              // Check if the cookie is set
              const cookies = document.cookie.split("; ").reduce((acc, cookie) => {
                const [key, value] = cookie.split("=");
                acc[key] = value;
                return acc;
              }, {});

              if (freeWBParam || cookies.FREEWB) {
                // Only check the first id for upsell logic
                const product = window.allProductUpsells.find((item) => item.id == ids[0]) ?? false;
                if (product && product.url.includes("suri-sustainable-sonic-toothbrush-uv-c-led-case")) {
                  const cartResponse = await fetch("/cart.js", {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                  });

                  if (cartResponse.ok) {
                    const cartData = await cartResponse.json();
                    const isItemInCart = cartData.items.some((item) => item.id === 55422926815611);

                    if (!isItemInCart) {
                      items.push({
                        id: 55422926815611,
                        quantity: 1,
                      });
                    }
                  }
                }
              }

              // Detect addon subscription items — attach _addon_parent_variant
              // so the cart can link this subscription to the parent product variant
              const isAddonSubscription = p.closest('[data-is-addon="true"][data-mode="add_item"]');
              let addonParentVariantId = null;
              if (isAddonSubscription) {
                const mainVariantInput = parent.querySelector('input[name="id"]') || document.getElementById('selected-variant-id');
                if (mainVariantInput) addonParentVariantId = mainVariantInput.value;
              }

              for (let i = 0; i < ids.length; i++) {
                let id = ids[i];
                if (id == "false") {
                  continue;
                }
                let itemSellingPlan = null;
                if (useSyncedSellingPlans) {
                  itemSellingPlan = sellingPlans[i] && sellingPlans[i] !== "null" && sellingPlans[i] !== "" ? sellingPlans[i] : null;
                } else if (sellingPlans.length > 0 && sellingPlans[0] !== "null" && sellingPlans[0] !== "") {
                  itemSellingPlan = sellingPlans[0];
                }
                // Target Subscription Variant: the active plan subscribes to a
                // DIFFERENT product's variant (flavour-matched via the plan link).
                // Swap the main line's variant id for the target when subscribing.
                if (i === 0 && itemSellingPlan && variant.dataset.subTargetVariant) {
                  id = variant.dataset.subTargetVariant;
                }
                let itemQuantity = 1;
                if (useSyncedQuantities) {
                  itemQuantity = parseInt(quantities[i], 10) || 1;
                } else if (quantities.length > 0 && quantities[0] !== "") {
                  itemQuantity = parseInt(quantities[0], 10) || 1;
                }
                const itemEntry = {
                  id,
                  quantity: itemQuantity,
                  ...(itemSellingPlan ? { selling_plan: itemSellingPlan } : {}),
                };
                if (addonParentVariantId && itemSellingPlan) {
                  itemEntry.properties = { _addon_parent_variant: addonParentVariantId };
                }
                // Fixed-price pack: carry the pack size so the cart badge can show
                // "Pack of N" despite the line quantity being 1.
                const _packQty = parseInt(variant.dataset.packQuantity || '0', 10);
                if (itemSellingPlan && _packQty > 1) {
                  itemEntry.properties = Object.assign({}, itemEntry.properties, { _pack_quantity: _packQty });
                }
                // Display Only Quantity: cosmetic pack size for the cart badge only —
                // never used in quantity or price calculations.
                const _displayPackQty = parseInt(variant.dataset.displayPackQuantity || '0', 10);
                if (itemSellingPlan && _displayPackQty > 1) {
                  itemEntry.properties = Object.assign({}, itemEntry.properties, { _display_pack_quantity: _displayPackQty });
                }
                items.push(itemEntry);
              }
            }
          }
        } else {
          const variantId = btn.dataset.defaultProduct;
          items.push({
            id: variantId,
            quantity: quantity,
          });
        }

        // Pick up extra subscription inputs (double-up, additional offer)
        // These are checked inputs that weren't the "first" per <product> element
        if (addProducts && parent.querySelector(".global-product product")) {
          const processedInputs = new Set();
          parent.querySelectorAll('.global-product product').forEach(p => {
            const first = p.querySelector('[data-product-selection]:checked');
            if (first) processedInputs.add(first);
          });
          const allChecked = parent.querySelectorAll('[data-product-selection]:checked');
          allChecked.forEach(input => {
            if (processedInputs.has(input)) return;
            if (!input.value || input.value === 'false') return;
            const spIds = (input.dataset.sellingPlan || '').split(',').filter(Boolean);
            const variantIds = input.value.split(',');
            const qtys = (input.dataset.quantity || '1').split(',');
            for (let i = 0; i < variantIds.length; i++) {
              if (!variantIds[i] || variantIds[i] === 'false') continue;
              const entry = { id: variantIds[i], quantity: parseInt(qtys[i] || qtys[0] || '1', 10) };
              if (spIds[i] && spIds[i] !== 'null') entry.selling_plan = spIds[i];
              else if (spIds[0] && spIds[0] !== 'null') entry.selling_plan = spIds[0];
              items.push(entry);
            }
          });
        }

        if (items.length === 0 || addProducts == false) return; // Exit if no items to add

        const shouldTrackUvCaseAtc = btn.dataset.convertUvAtcEnabled === "true" && this.hasUvCaseSelection(parent);
        if (shouldTrackUvCaseAtc) {
          this.triggerConvertEvent("atc_uv_case");
        }

        const shouldTrackSubscriptionAtc = this.hasActivePaidSubscriptionSelection(parent);
        if (shouldTrackSubscriptionAtc) {
          this.triggerConvertEvent("atc_subscription");
        }

        try {
          // Add items to cart
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ items }),
          });

          if (!response.ok) throw new Error("Failed to add to cart");

          window.PXUTheme.jsCart.updateCart();
          window.PXUTheme.jsCart.toggleCart("show");

          btn.classList.remove("loading");
          this.reloadProductForm();
        } catch (error) {
          console.error("Error adding to cart:", error);
          btn.classList.remove("loading");
        }
      });
    }
  },

  clearInputs() {
    const allInputs = document.querySelectorAll("product input:checked");

    for (const input of allInputs) {
      if (input.dataset.defaultOption === "true") continue;
      input.checked = false;

      if (input.dataset.colour && input.closest("product")) {
        const product = input.closest("product");
        const colour = input.dataset.colour;
        if (product.querySelector(`input[data-colour="${colour}"][data-default-option]`)) {
          const defaultInput = product.querySelector(`input[data-colour="${colour}"][data-default-option]`);
          defaultInput.checked = true;
        }
      }
    }

    // Note: p_global_subscription resets itself via the 'productSelectionsCleared' event

    if (document.querySelector("[data-default-price]")) {
      for (const priceEl of document.querySelectorAll("[data-default-price]")) {
        if (priceEl.dataset.defaultPrice) {
          priceEl.textContent = priceEl.dataset.defaultPrice;
        }
      }
    }
    
    // Reset subscription activation flag so it preselects again on next selection
    this._subscriptionActivated = false;
    
    // Reset user preferences so defaults can apply again
    window.subscriptionPreference = null;
    
    // Clear "Also Included" selected state
    const alsoIncludedItems = document.querySelectorAll('[data-also-included-item]');
    alsoIncludedItems.forEach(item => {
      item.classList.remove('selected');
      item.classList.remove('active');
    });
    
    // Reset the also included heading selection state
    const alsoIncludedHeading = document.querySelector('.global-product__addons .heading[data-sticky-selection]');
    if (alsoIncludedHeading) {
      alsoIncludedHeading.setAttribute('data-selection', '');
      alsoIncludedHeading.setAttribute('data-sticky-selection', '');
    }
    
    // Set flag to prevent subscription preselection during reset
    window._resettingSelections = true;
    
    // Mark variant input as unselected initially (will be updated by resetToDefaults if colors are re-selected)
    const variantInput = document.getElementById('selected-variant-id');
    if (variantInput) {
      variantInput.setAttribute('data-options-unselected', '');
    }
    
    // Also clear color radio buttons in variant-options form
    const variantOptionsForm = document.getElementById('variant-options');
    if (variantOptionsForm) {
      const colorInputs = variantOptionsForm.querySelectorAll('input[type="radio"][data-swapper]');
      colorInputs.forEach(input => {
        input.checked = false;
      });
      
      // Reset the variant options groups to initial progressive disclosure state
      const optionGroups = variantOptionsForm.querySelectorAll('.product_variant_option');
      optionGroups.forEach((group, index) => {
        if (index === 0) {
          group.classList.add('available');
        } else {
          group.classList.remove('available');
        }
      });
    }
    
    // Dispatch event — SubscriptionBlockController listens and handles its own reset
    document.dispatchEvent(new CustomEvent('productSelectionsCleared', { bubbles: true }));
    
    setTimeout(function() {
      window._resettingSelections = false;
    }, 300);
  },

  captureInitialFormState() {
    const form = document.querySelector('.global-product .global-product__blocks');
    if (form) {
      this._initialFormHTML = form.innerHTML;
    }
  },

  reloadProductForm() {
    const form = document.querySelector('.global-product .global-product__blocks');
    if (!form || !this._initialFormHTML) {
      this.resetFloatingATC();
      this.clearInputs();
      this.disableDisabledBlocks();
      return;
    }

    // Restore the pristine server-rendered HTML
    form.innerHTML = this._initialFormHTML;

    // Reset global state
    window.subscriptionPreference = null;
    window.headOptionsPreference = null;
    window._resettingSelections = false;

    // Re-execute inline scripts so variant-options.liquid and subscription JS
    // re-bind. We MUST preserve every attribute on the script tag, not just
    // `src` — otherwise `<script type="application/json" data-variant-options-config>`
    // (and similar data-tagged JSON config blocks) lose their `type` and
    // `data-*` attributes and either get evaluated as JavaScript or become
    // unreachable to the consumers that look them up by attribute. Skipping
    // the type/attribute copy was the root cause of `VariantPicker._readConfig()`
    // returning an empty object after the first add-to-cart, which left the
    // newly rendered #variant-options without variant data or click handlers.
    form.querySelectorAll('script').forEach(function(oldScript) {
      var newScript = document.createElement('script');
      for (var i = 0; i < oldScript.attributes.length; i++) {
        var attr = oldScript.attributes[i];
        newScript.setAttribute(attr.name, attr.value);
      }
      if (!oldScript.src) {
        newScript.textContent = oldScript.textContent;
      }
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });

    // Re-init all global product handlers on the fresh DOM
    this.onLoadElements();
    this.disableDisabledBlocks();
    this.resetFloatingATC();

    this.watchSwappers();

    if (document.querySelector("[data-product-selection]")) {
      this.watchInputs();
    }
    if (document.querySelector("[data-product-atc]")) {
      this.watchATCs();
    }
    if (document.querySelectorAll("input[data-update-selected-variant]")) {
      this.watchSelectedVariantChanges();
    }

    this.initProgressiveDisclosure();
    this.initSubscriptionBlocks();
    this.outOfStockHandler(false);
    this.positionBackInStockWidget();
    this.updateKlarnaPrice();
    this.watchMainATCTextChanges();
    this.watchOutOfStockTextChanges();

    // Re-bootstrap the variant-picker controllers. The deferred bundle script
    // only runs its top-level bootstrap once per page; without an explicit
    // re-init here the freshly restored #variant-options DOM has no listener
    // wiring, which breaks the kit selector, conditional visibility, and the
    // ATC readiness gate after the first add-to-cart.
    if (window.PXUTheme && typeof window.PXUTheme.bootstrapVariantPickers === 'function') {
      window.PXUTheme.bootstrapVariantPickers();
    }

    if (document.querySelectorAll("[data-sticky-selection]") && document.getElementById("getStartedBtn")) {
      this.updateFloatingATC();
    }

    if (document.body.getAttribute("data-intelligems-enabled") === "true") {
      this.initIntelligems();
    }
  },

  watchATCScroll() {
    let currentScroll = window.scrollY;
    let element = document.querySelector(".global-product__atc-bar");

    const checkATCScroll = () => {
      if (window.scrollY > 0) {
        element.classList.remove("scrolled");
      } else {
        element.classList.add("scrolled");
      }

      if (window.scrollY > currentScroll) {
        element.classList.add("show");
      } else {
        element.classList.remove("show");
      }

      currentScroll = window.scrollY;

      const btn = element.querySelector(".btn");
      const productSection = document.querySelector(".global-section-parent.global-product");
      btn.addEventListener("click", (event) => {
        // event.preventDefault();
        document.querySelector("#header").classList.remove("show");
        element.classList.remove("show");
        // productSection.scrollIntoView({ behavior: "smooth" });
      });
    };

    // Run the check immediately on page load
    checkATCScroll();

    // Add the scroll event listener
    document.addEventListener("scroll", checkATCScroll);
  },

  updateFloatingATC() {
    const getStartedBtn = document.getElementById("getStartedBtn");
    if (!getStartedBtn) return;
    const getStartedBtnText = getStartedBtn.querySelector("span.text");

    const addToCartLabel = this.getButtonLabel("addToCart", getStartedBtn.dataset.labelAddToCart || (getStartedBtnText ? getStartedBtnText.textContent.trim() : ""));
    const floatingAddToCartLabel = this.getButtonLabel("floatingAddToCart", addToCartLabel);
    const floatingOutOfStockLabel = this.getButtonLabel("floatingOutOfStock", this.getButtonLabel("outOfStock", getStartedBtn.dataset.labelOutOfStock || ""));
    const floatingDefaultLabel = this.getButtonLabel("floatingDefault", getStartedBtn.dataset.labelDefault || (getStartedBtnText ? getStartedBtnText.textContent.trim() : ""));

    // Helper function to find the current incomplete step
    const findCurrentTarget = () => {
      const selectEls = document.querySelectorAll("[data-sticky-selection]");
      let currentTargetEl = selectEls[0];

      while (currentTargetEl && (currentTargetEl.dataset.stickySelection === "selected" || currentTargetEl.dataset.selection === "selected")) {
        const nextIndex = Array.from(selectEls).indexOf(currentTargetEl) + 1;
        currentTargetEl = selectEls[nextIndex] || null;
      }
      
      return currentTargetEl;
    };

    // Update button text
    const currentTargetEl = findCurrentTarget();
    
    // Check if current step is the ATC button itself (last step) or if all steps are complete
    const isAtcStep = currentTargetEl && currentTargetEl.dataset.stickySelectionTitle === floatingAddToCartLabel;
    
    if (currentTargetEl && !isAtcStep) {
      // There's an incomplete step before the ATC button
      getStartedBtnText.textContent = currentTargetEl.dataset.stickySelectionTitle;
      getStartedBtn.classList.remove("disabled");
      getStartedBtn.setAttribute("aria-disabled", "false");
    } else {
      // All steps are selected - check if the selection is available
      const inputs = document.querySelectorAll("[data-product-selection]");
      let checkedOutOfStock = false;
      let hasOptionsUnselected = false;
      
      for (const input of inputs) {
        if (input.checked) {
          if (input.dataset.outOfStock === "true") {
            checkedOutOfStock = true;
          }
          if (input.hasAttribute("data-options-unselected")) {
            hasOptionsUnselected = true;
          }
        }
      }
      
      // Mirror main ATC button state
      const mainATC = document.querySelector("[data-product-atc]");
      const mainATCOutOfStockLabel = this.getButtonLabel("outOfStock", mainATC?.dataset?.oosLabel || floatingOutOfStockLabel);
      const mainATCDisabled = mainATC && (mainATC.classList.contains("disabled") || (mainATCOutOfStockLabel && mainATC.textContent.trim() === mainATCOutOfStockLabel));
      
      if (hasOptionsUnselected) {
        // Options are not fully selected - find the next unselected option
        const nextUnselected = findCurrentTarget();
        if (nextUnselected) {
          getStartedBtnText.textContent = nextUnselected.dataset.stickySelectionTitle;
          getStartedBtn.classList.remove("disabled");
          getStartedBtn.setAttribute("aria-disabled", "false");
        } else {
          getStartedBtnText.textContent = floatingAddToCartLabel || floatingDefaultLabel;
          getStartedBtn.classList.add("disabled");
          getStartedBtn.setAttribute("aria-disabled", "true");
        }
      } else if (checkedOutOfStock || mainATCDisabled) {
        getStartedBtnText.textContent = floatingOutOfStockLabel || mainATCOutOfStockLabel || floatingAddToCartLabel;
        getStartedBtn.classList.add("disabled");
        getStartedBtn.setAttribute("aria-disabled", "true");
      } else {
        const subLabel = this.getActiveSubscriptionLabel();
        getStartedBtnText.textContent = subLabel || floatingAddToCartLabel || floatingDefaultLabel;
        getStartedBtn.classList.remove("disabled");
        getStartedBtn.setAttribute("aria-disabled", "false");
      }
    }

    // Only add event listeners once
    if (!this._floatingATCInitialized) {
      this._floatingATCInitialized = true;
      
      // Use event delegation on document to handle dynamically loaded elements
      document.addEventListener("click", (e) => {
        const el = e.target.closest("[data-sticky-selection]");
        if (!el) return;
        
        const input = el.querySelector("input:checked, input[selected]");
        if (!input) {
          return false;
        }

        if (el.getAttribute("data-selection") === "selected") {
          return false;
        }

        el.setAttribute("data-selection", "selected");
        el.setAttribute("data-sticky-selection", "selected");

        // Find the next element
        const selectEls = document.querySelectorAll("[data-sticky-selection]");
        const currentIndex = Array.from(selectEls).indexOf(el);
        const nextEl = selectEls[currentIndex + 1];
        
        if (nextEl) {
          // Note: stick to one attribute naming convention
          const nextTitle = nextEl.dataset.stickySelectionTitle || nextEl.dataset.selectionTitle;
          if (nextTitle) {
            getStartedBtnText.textContent = nextTitle;
            getStartedBtn.classList.remove("disabled");
            getStartedBtn.setAttribute("aria-disabled", "false");
          }
        } else {
          // This was the last step - check availability
          const inputs = document.querySelectorAll("[data-product-selection]");
          let checkedOutOfStock = false;
          
          for (const input of inputs) {
            if (input.checked && input.dataset.outOfStock === "true") {
              checkedOutOfStock = true;
              break;
            }
          }
          
          const mainATC = document.querySelector("[data-product-atc]");
          const mainATCOutOfStockLabel = this.getButtonLabel("outOfStock", mainATC?.dataset?.oosLabel || floatingOutOfStockLabel);
          const mainATCDisabled = mainATC && (mainATC.classList.contains("disabled") || (mainATCOutOfStockLabel && mainATC.textContent.trim() === mainATCOutOfStockLabel));
          
          if (checkedOutOfStock || mainATCDisabled) {
            getStartedBtnText.textContent = floatingOutOfStockLabel || mainATCOutOfStockLabel || floatingAddToCartLabel;
            getStartedBtn.classList.add("disabled");
            getStartedBtn.setAttribute("aria-disabled", "true");
          } else {
            getStartedBtnText.textContent = floatingAddToCartLabel || floatingDefaultLabel;
            getStartedBtn.classList.remove("disabled");
            getStartedBtn.setAttribute("aria-disabled", "false");
          }
        }
      });

      getStartedBtn.addEventListener("click", (event) => {
        // event.preventDefault();
        const getStartedText = getStartedBtnText.textContent.trim();

        if (getStartedText === (floatingOutOfStockLabel || this.getButtonLabel("outOfStock"))) {
          // Don't do anything if out of stock
          return;
        }

        if (getStartedText === (floatingAddToCartLabel || addToCartLabel)) {
          const addToCartBtn = document.querySelector("[data-product-atc]");
          if (addToCartBtn) {
            addToCartBtn.click(); // Triggers the native click event
          }
          return; // Don't scroll, just add to cart
        }

        // Find the current incomplete step dynamically
        const targetEl = findCurrentTarget();
        if (!targetEl) return;

        const offset = 51;
        const rect = targetEl.getBoundingClientRect();
        const scrollTop = window.scrollY || window.pageYOffset;
        const targetTop = rect.top + scrollTop - offset;

        const header = document.querySelector("#header");
        let lastScrollTime = Date.now();
        let keepRemoving = true;

        const suppressHeader = () => {
          if (!keepRemoving) return;
          header.classList.remove("show");
          if (Date.now() - lastScrollTime > 500) {
            keepRemoving = false;
            return;
          }
          requestAnimationFrame(suppressHeader);
        };

        const scrollHandler = () => {
          lastScrollTime = Date.now();
          header.classList.remove("show");
        };

        window.addEventListener("scroll", scrollHandler);
        requestAnimationFrame(suppressHeader);

        window.scrollTo({
          top: targetTop,
          behavior: "smooth",
        });

        targetEl.classList.add("Attention");
        setTimeout(() => {
          targetEl.classList.remove("Attention");
        }, 2000);
      });
    }
  },

  resetFloatingATC() {
    const selectEls = document.querySelectorAll("[data-sticky-selection]");
    selectEls.forEach((el) => {
      el.setAttribute("data-sticky-selection", "");
      el.setAttribute("data-selection", "");
    });
    this.updateFloatingATC();
  },

  initIntelligems() {
    const waitForIntelligems = () => {
      return new Promise((resolve) => {
        const checkExist = setInterval(() => {
          if (window.igData && Object.keys(window.igData).length > 0) {
            clearInterval(checkExist);
            resolve();
          }
        }, 100);
      });
    }

    waitForIntelligems().then(() => {
      if (window.igData) {

        // Update data-price and data-compare-price strings where data-ig-datatags is equal to the variant ID 
        const igDataTags = document.querySelectorAll("[data-ig-datatags]");
        
        igDataTags.forEach((tag) => {
          const id = tag.dataset.igDatatags;
          const price = window.igData?.price?.getPriceByVariantId(id) * 100;
          const comparePrice = window.igData?.price?.getComparePriceByVariantId(id) * 100;
          if (price && price > 0) {
            tag.setAttribute("data-price", price);
          }
          if (comparePrice && comparePrice > 0) {
            tag.setAttribute("data-compare-price", comparePrice);
          }
        });

        // Update text elements showing prices where the data-ig-pricetext and data-ig-comparetext attributes are set to the variant ID 
        const igPriceTexts = document.querySelectorAll("[data-ig-pricetext]");
        igPriceTexts.forEach((text) => {
          const id = text.dataset.igPricetext;
          const price = window.igData?.price?.getPriceByVariantId(id);
          if (price && price > 0) {
            text.textContent = document.body.getAttribute("data-money-format").replace("{{amount}}", price);
          }
        });

        const igCompareTexts = document.querySelectorAll("[data-ig-comparetext]");
        igCompareTexts.forEach((text) => {
          const id = text.dataset.igComparetext;
          const comparePrice = window.igData?.price?.getComparePriceByVariantId(id);
          if (comparePrice && comparePrice > 0) {
            text.textContent = document.body.getAttribute("data-money-format").replace("{{amount}}", comparePrice);
          }
        });

        const igSavingsTexts = document.querySelectorAll("[data-ig-savingstext]");
        igSavingsTexts.forEach((text) => {
          const id = text.dataset.igSavingstext;
          const price = window.igData?.price?.getPriceByVariantId(id);
          const comparePrice = window.igData?.price?.getComparePriceByVariantId(id);
          const savings = Math.round(((comparePrice - price) / comparePrice) * 100);
          if (savings && savings > 0) {
            text.textContent = `${savings}% OFF`;
          }
        });

      }
    });
  },

  onLoadElements() {
    document.querySelectorAll('[data-loaded="false"]').forEach(el => {
      el.setAttribute('data-loaded', 'true');
    });
    document.querySelectorAll('.not-loaded').forEach(el => {
      el.classList.remove('not-loaded');
    });
  },

  positionBackInStockWidget() {
    const bis = document.getElementById('backInStockWidget');
    if (!bis) return;
    const containers = document.querySelectorAll('.global-product__options');
    const target = containers[containers.length - 1];
    if (target && bis.parentElement !== target) {
      target.appendChild(bis);
    }
  },

  /**
   * Show/hide #backInStockWidget without restarting the slide animation on
   * every outOfStockHandler pass. Model→Core repair fires many intermediate
   * OOS/unselected states; debounce + no-op when already in the target state.
   */
  setBackInStockWidgetVisible(shouldShow, variantId) {
    const bis = document.getElementById('backInStockWidget');
    if (!bis) return;

    this._bisDesiredVisible = !!shouldShow;
    if (variantId) this._bisDesiredVariantId = String(variantId);

    if (this._bisDebounceTimer) clearTimeout(this._bisDebounceTimer);
    this._bisDebounceTimer = setTimeout(() => {
      this._bisDebounceTimer = null;
      this._applyBackInStockWidgetVisibility();
    }, 80);
  },

  _applyBackInStockWidgetVisibility() {
    const bis = document.getElementById('backInStockWidget');
    if (!bis) return;

    const shouldShow = !!this._bisDesiredVisible;
    const variantId = this._bisDesiredVariantId;
    const isShown =
      bis.style.display !== 'none' && bis.classList.contains('is-visible');

    if (shouldShow) {
      if (variantId) bis.setAttribute('data-variant-id', variantId);

      if (this._bisHideTimer) {
        clearTimeout(this._bisHideTimer);
        this._bisHideTimer = null;
      }

      // Already visible — only refresh the variant id, don't restart animation.
      if (isShown) return;

      if (this._bisShowRaf) {
        cancelAnimationFrame(this._bisShowRaf);
        this._bisShowRaf = null;
      }

      bis.style.display = '';
      // Force a layout frame so the transition runs from the hidden state
      // when coming from display:none, but only on a real hide→show edge.
      void bis.offsetWidth;
      this._bisShowRaf = requestAnimationFrame(() => {
        this._bisShowRaf = null;
        if (this._bisDesiredVisible) bis.classList.add('is-visible');
      });
      return;
    }

    if (!isShown && bis.style.display === 'none') return;

    if (this._bisShowRaf) {
      cancelAnimationFrame(this._bisShowRaf);
      this._bisShowRaf = null;
    }

    bis.classList.remove('is-visible');
    if (this._bisHideTimer) clearTimeout(this._bisHideTimer);
    this._bisHideTimer = setTimeout(() => {
      this._bisHideTimer = null;
      if (!this._bisDesiredVisible) bis.style.display = 'none';
    }, 320);
  },

  initProgressiveDisclosure() {
    // Handle variant-options progressive disclosure. A page (notably the
    // bundle builder) can render MULTIPLE #variant-options forms — one per
    // product step. They must be gated as ONE ordered sequence so that a
    // later product's first option stays locked until every earlier step is
    // chosen. (getElementById would only ever wire the first form.)
    const variantOptionsForms = Array.from(document.querySelectorAll('#variant-options'))
      .filter(form => form.dataset.progressiveDisclosure === 'true');
    if (variantOptionsForms.length) {
      this.initVariantOptionsDisclosure(variantOptionsForms);
    }

    // Handle toothbrush-options progressive disclosure
    const toothbrushContainers = document.querySelectorAll('.toothbrush-options-block[data-progressive-disclosure="true"]');
    toothbrushContainers.forEach(container => {
      this.initToothbrushOptionsDisclosure(container);
    });

    // Subscription progressive disclosure is handled by SubscriptionBlockController
  },

  initVariantOptionsDisclosure(forms) {
    // Accept a single form (legacy single-product pages) or an array of forms
    // (bundle builder). All of their option groups form one ordered chain.
    const formList = Array.isArray(forms) ? forms : [forms];
    if (!formList.length) return;

    const hasSelection = (el) => {
      const inputs = el.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      return Array.from(inputs).some(input => input.checked);
    };

    // The bundle builder renders each product step's subscription block INSIDE
    // its #variant-options form. Those "Choose your plan" steps are part of the
    // ordered sequence too: a later product's options must stay locked until
    // every earlier step — INCLUDING earlier products' plans — has a selection.
    // So gate against the full [data-sticky-selection] step list (variant
    // option groups AND subscription fieldsets), in document order, scoped to
    // the bundle. The subscription wrapper itself also picks up
    // data-sticky-selection when selected, so skip it to avoid double counting
    // (its fieldset carries the real step marker). Only .product_variant_option
    // steps toggle `.available`; subscription steps gate via data-locked
    // (handled by SubscriptionBlockController) and only contribute their
    // selection state here.
    const bundle = formList[0].closest('[data-bundle-builder]');

    let getSteps;
    if (bundle) {
      // Scope to product steps only ([data-bb-step]) so optional add-ons and
      // the totals/ATC row never gate the product sequence.
      getSteps = () =>
        Array.from(
          bundle.querySelectorAll('[data-bb-step] [data-sticky-selection]')
        ).filter((el) => !el.classList.contains('subscription-options-block'));
    } else {
      // Legacy single-product: keep the original option-group-only chain so
      // existing PDPs behave exactly as before.
      const groups = [];
      formList.forEach((form) => {
        form
          .querySelectorAll('.product_variant_option')
          .forEach((group) => groups.push(group));
      });
      if (!groups.length) return;
      getSteps = () => groups;
    }

    // A step that is hidden by the kit selector (e.g. the Bottle Colour upsell
    // when "Refills only" is chosen) must be ignored entirely: it should neither
    // be gated nor break the chain for the steps that follow it. Otherwise its
    // missing selection would leave later steps locked and block ATC validation.
    const isStepHidden = (el) =>
      !!el.closest('[data-hidden-by-kit="true"], [data-upsell-for-kit][hidden]');

    // Strict chain: a variant option group is available only when EVERY
    // preceding step has a selection. Deselecting an earlier step (or leaving a
    // plan unchosen) re-locks everything after it.
    const updateGroups = () => {
      let allPrevSelected = true;
      getSteps().forEach((el) => {
        if (isStepHidden(el)) return;
        const isVariantGroup = el.classList.contains('product_variant_option');
        if (isVariantGroup) {
          // Only gate groups inside a progressive form. A non-progressive step
          // (freely selectable) still contributes its selection state to the
          // chain below, but must never be locked.
          const form = el.closest('#variant-options');
          const isProgressive =
            !form || form.dataset.progressiveDisclosure === 'true';
          if (isProgressive) {
            if (allPrevSelected) {
              el.classList.add('available');
            } else {
              el.classList.remove('available');
            }
          }
        }
        if (!hasSelection(el)) allPrevSelected = false;
      });
    };

    updateGroups();
    setTimeout(updateGroups, 0);

    // In the bundle builder, subscription plan changes bubble to the bundle
    // root (the block is inside the form), so one listener on the bundle covers
    // every step. On legacy pages keep the per-form listeners.
    const changeTargets = bundle ? [bundle] : formList;
    changeTargets.forEach((target) => {
      target.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"], input[type="checkbox"]')) {
          updateGroups();
        }
      });
    });
  },

  initToothbrushOptionsDisclosure(container) {
    const optionGroups = container.querySelectorAll('.product-bundle__option');
    if (!optionGroups.length) return;

    const hasSelection = (group) => {
      const inputs = group.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      return Array.from(inputs).some(input => input.checked);
    };

    const updateGroups = () => {
      let allowNext = true;
      optionGroups.forEach((group, index) => {
        if (index === 0) {
          group.classList.add('available');
          allowNext = hasSelection(group);
          return;
        }

        if (allowNext) {
          group.classList.add('available');
          allowNext = hasSelection(group);
        } else {
          group.classList.remove('available');
        }
      });
    };

    updateGroups();
    setTimeout(updateGroups, 0);

    container.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"], input[type="checkbox"]')) {
        updateGroups();
      }
    });

    document.addEventListener('variantColorSelected', updateGroups);
  },

  // initSubscriptionDisclosure and initPreselectedOptions have been replaced
  // by SubscriptionBlockController (managed via initSubscriptionBlocks)

  watchMainATCTextChanges() {
    if (this._mainATCObserver) this._mainATCObserver.disconnect();
    
    const mainATC = document.querySelector("[data-product-atc]");
    const getStartedBtn = document.getElementById("getStartedBtn");
    const getStartedBtnText = getStartedBtn?.querySelector("span.text");

    if (!mainATC || !getStartedBtn || !getStartedBtnText) return;

    // Store the last known text to avoid unnecessary updates
    let lastMainATCText = mainATC.textContent.trim();

    // Create a MutationObserver to watch for text changes on the main ATC button
    const observer = new MutationObserver((mutations) => {
      const currentText = mainATC.textContent.trim();
      
      // Only update if the text actually changed
      if (currentText !== lastMainATCText) {
        lastMainATCText = currentText;
        
        // Check if we should update the sticky button
        // Only update if all option fieldsets (not including the ATC button itself) are selected
        const optionFieldsets = document.querySelectorAll('[data-sticky-selection]:not([data-product-atc])');
        const allOptionsSelected = Array.from(optionFieldsets).every(fieldset => 
          fieldset.dataset.stickySelection === 'selected'
        );
        
        if (allOptionsSelected) {
          // Update the sticky button text to match the main button
          // But preserve the step-by-step flow for incomplete selections
          const floatingAddToCartLabel = this.getButtonLabel("floatingAddToCart", currentText);
          const floatingDefaultLabel = this.getButtonLabel("floatingDefault", "");
          
          // Only update if the current sticky text is the add to cart label (not a step instruction)
          const currentStickyText = getStartedBtnText.textContent.trim();
          const isShowingATCText = currentStickyText === floatingAddToCartLabel || 
                                   currentStickyText === floatingDefaultLabel ||
                                   currentStickyText === this.buttonLabels.addToCart;
          
          if (isShowingATCText) {
            getStartedBtnText.textContent = currentText;
          }
        }
      }
    });

    observer.observe(mainATC, {
      characterData: true,
      childList: true,
      subtree: true
    });
    this._mainATCObserver = observer;
  },

  watchOutOfStockTextChanges() {
    if (this._oosObservers) {
      this._oosObservers.forEach(o => o.disconnect());
    }
    this._oosObservers = [];
    
    const mainATC = document.querySelector("[data-product-atc]");
    const getStartedBtn = document.getElementById("getStartedBtn");
    const getStartedBtnText = getStartedBtn?.querySelector("span.text");

    if (!mainATC) return;

    // Helper function to check if text indicates out of stock
    const isOutOfStockText = (text) => {
      if (!text) return false;
      const normalizedText = text.toLowerCase().trim();
      
      // Check for various out of stock patterns (case-insensitive)
      // English variations
      if (normalizedText.includes('out of stock') || 
          normalizedText.includes('out-of-stock') ||
          normalizedText.includes('outofstock')) {
        return true;
      }
      
      // Other language variations (from locale files)
      const oosPatterns = [
        'fora de estoque',      // Portuguese
        '缺货',                  // Chinese
        'en rupture de stock',  // French
        'ausverkauft',          // German
        'agotado',              // Spanish
        'sold out',             // Alternative English
        'unavailable'           // Generic unavailable
      ];
      
      return oosPatterns.some(pattern => normalizedText.includes(pattern));
    };

    // Helper function to disable a button
    const disableButton = (button) => {
      if (button) {
        button.classList.add('disabled');
        button.setAttribute('aria-disabled', 'true');
      }
    };

    // Watch main ATC button
    const mainATCObserver = new MutationObserver(() => {
      const currentText = mainATC.textContent.trim();
      if (isOutOfStockText(currentText)) {
        disableButton(mainATC);
      }
    });

    mainATCObserver.observe(mainATC, {
      characterData: true,
      childList: true,
      subtree: true
    });
    this._oosObservers.push(mainATCObserver);

    if (getStartedBtn && getStartedBtnText) {
      const stickyATCObserver = new MutationObserver(() => {
        const currentText = getStartedBtnText.textContent.trim();
        if (isOutOfStockText(currentText)) {
          disableButton(getStartedBtn);
        }
      });

      stickyATCObserver.observe(getStartedBtnText, {
        characterData: true,
        childList: true,
        subtree: true
      });

      stickyATCObserver.observe(getStartedBtn, {
        characterData: true,
        childList: true,
        subtree: true
      });
      this._oosObservers.push(stickyATCObserver);
    }

    // Also watch and disable additional payment buttons when main ATC is out of stock
    const additionalPaymentContainer = document.querySelector('.additional_payment_container');
    if (additionalPaymentContainer) {
      const paymentObserver = new MutationObserver(() => {
        const currentText = mainATC.textContent.trim();
        if (isOutOfStockText(currentText)) {
          // Hide the additional payment container
          additionalPaymentContainer.classList.add('hidden');
          // Also disable any buttons inside it
          const paymentButtons = additionalPaymentContainer.querySelectorAll('button, [role="button"]');
          paymentButtons.forEach(btn => disableButton(btn));
        }
      });

      paymentObserver.observe(mainATC, {
        characterData: true,
        childList: true,
        subtree: true
      });
    }

    // Initial check on page load
    setTimeout(() => {
      if (mainATC && isOutOfStockText(mainATC.textContent.trim())) {
        disableButton(mainATC);
        if (additionalPaymentContainer) {
          additionalPaymentContainer.classList.add('hidden');
        }
      }
      if (getStartedBtn && getStartedBtnText && isOutOfStockText(getStartedBtnText.textContent.trim())) {
        disableButton(getStartedBtn);
      }
    }, 100);
  }
};

document.addEventListener("DOMContentLoaded", () => {
  window.PXUTheme.jsGlobalProduct.init();
  
  if (document.querySelector(".global-product .shopify-payment-button")) {
    // Get the payment button element
    const paymentButton = document.querySelector(".global-product .shopify-payment-button");
    // Get the target container where the payment button should be moved
    const targetContainer = document.querySelector(".global-product .additional_payment_container");

    // If the container exists, move the payment button into it
    if (targetContainer) {
      targetContainer.appendChild(paymentButton);
    }
  }
  
  // Toggle payment buttons based on initial subscription selection
  // Use a small delay to ensure payment buttons are rendered
  setTimeout(() => {
    const paymentButtons = document.querySelectorAll('.shopify-payment-button, .additional_payment_container');
    if (paymentButtons.length > 0) {
      const checkedInputs = document.querySelectorAll(
        'input[name="subscription-plan"]:checked, input[name="selling_plan_option"]:checked'
      );
      
      let hasSubscription = false;
      checkedInputs.forEach(input => {
        if (input.dataset.sellingPlan) {
          hasSubscription = true;
        }
      });
      
      paymentButtons.forEach(button => {
        if (hasSubscription) {
          button.style.display = 'none';
        } else {
          button.style.display = '';
        }
      });
    }
  }, 500);
});
